/**
 * Product Components - Standalone version
 * Contains: variant-select, variant-select-card, variant-select-section,
 * product-variant, product-form, sticky-atc, product-purchase-options
 */

(function() {
  // Early exit if already registered
  if (customElements.get('variant-select')) return;

  // V8-pattern: persist the user's subscribe/one-time choice per product so that
  // switching size on a collection card (which re-inits the PPO with the new
  // variant's data attrs) doesn't reset the selection. V8 uses
  // window._v8PurchaseType (single value, one product visible at a time);
  // collection grids show many cards simultaneously, so we key by product id.
  window._cardPurchaseType = window._cardPurchaseType || {};

  // ============================================================================
  // Helper Functions (inlined from helper modules)
  // ============================================================================

  function fetchConfig(type = 'json') {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: `application/${type}` }
    };
  }

  // AU and NZ share the /en-au/ URL prefix, so a fetch URL alone can't
  // distinguish them — the server falls back to the localization cookie,
  // which can disagree with the country the page was rendered with (country
  // picker apps and market previews set it per-render). Pin every dynamic
  // price fetch to the rendered context via Shopify.country.
  function appendCountryParam(url) {
    const country = window.Shopify && window.Shopify.country;
    if (!country) return url;
    const separator = url.includes('?') ? '&' : '?';
    return url + separator + 'country=' + encodeURIComponent(country);
  }

  // Rivo "You'll earn X points" line under the PDP ATC
  // (snippets/v8-rivo-earn.liquid). Subscribe uses the subscriber rate rendered
  // by Liquid (not in Rivo's storefront config); one-time prefers the live
  // "Make a Purchase" rate, falling back to the Liquid rate. Rivo rounds order
  // earnings to whole points.
  function updateRivoEarnPoints(container, priceCents, isSub) {
    const earnEl = container && container.querySelector('[data-rivo-earn]');
    if (!earnEl) return;
    const pointsEl = earnEl.querySelector('[data-rivo-earn-points]');
    if (!pointsEl) return;

    let rate = parseFloat(earnEl.getAttribute('data-points-per-dollar')) || 0;
    const rule = window.Rivo?.loy_config?.frontend?.potential_points?.order_placed;
    if (rule && rule.enabled && rule.points_type === 'multiplier' && rule.points_amount > 0) {
      rate = rule.points_amount / (rule.currency_base_amount || 1);
    }
    if (isSub) {
      rate = parseFloat(earnEl.getAttribute('data-sub-points-per-dollar')) || rate;
    }

    const points = Math.round((priceCents / 100) * rate);
    pointsEl.textContent = points.toLocaleString('en-US') + ' points';
    earnEl.hidden = points <= 0;
  }

  // PubSub system
  const productPubSub = (function () {
    let subscribers = {};

    function subscribe(eventName, callback) {
      if (subscribers[eventName] === undefined) {
        subscribers[eventName] = [];
      }
      subscribers[eventName] = [...subscribers[eventName], callback];

      return function unsubscribe() {
        subscribers[eventName] = subscribers[eventName].filter((cb) => {
          return cb !== callback;
        });
      };
    }

    function publish(eventName, data) {
      if (subscribers[eventName]) {
        subscribers[eventName].forEach((callback) => {
          callback(data);
        });
      }
    }

    return { subscribe, publish };
  })();

  const PUB_SUB_EVENTS = {
    cartUpdate: 'cart-update',
    quantityUpdate: 'quantity-update',
    optionValueSelectionChange: 'option-value-selection-change',
    variantChange: 'variant-change',
    cartError: 'cart-error',
    purchaseTypeChange: 'purchase-type-change'
  };

  // V8 Product Cache — pre-fetches all linked product API templates on page load
  // to eliminate network lag when switching sizes/formulas.
  const V8ProductCache = {
    _cache: {},
    _fetching: {},

    /**
     * Build a cache key from a product URL.
     * Single template (api-v8) — no viewName dimension needed.
     */
    _key(productUrl) {
      return productUrl.split('?')[0];
    },

    /**
     * Pre-fetch a single product URL with the api-v8 template.
     * Returns a promise that resolves to the HTML string.
     */
    _fetchAndStore(productUrl) {
      const key = this._key(productUrl);
      if (this._cache[key]) return Promise.resolve(this._cache[key]);
      if (this._fetching[key]) return this._fetching[key];

      const separator = productUrl.includes('?') ? '&' : '?';
      const fetchUrl = appendCountryParam(productUrl + separator + 'view=api-v8');

      this._fetching[key] = fetch(fetchUrl)
        .then(r => r.ok ? r.text() : Promise.reject('fetch failed'))
        .then(html => {
          this._cache[key] = html;
          delete this._fetching[key];
          return html;
        })
        .catch(err => {
          delete this._fetching[key];
          console.warn('V8ProductCache: failed to pre-fetch', fetchUrl, err);
          return null;
        });

      return this._fetching[key];
    },

    /**
     * Get cached HTML for a product URL. Returns null if not cached.
     */
    get(productUrl) {
      const key = this._key(productUrl);
      return this._cache[key] || null;
    },

    /**
     * Get cached HTML, or fetch if not cached yet. Returns a promise.
     */
    getOrFetch(productUrl) {
      const cached = this.get(productUrl);
      if (cached) return Promise.resolve(cached);
      return this._fetchAndStore(productUrl);
    },

    /**
     * Scan the page for all product-variant elements and pre-fetch
     * the api-v8 template for each unique product URL.
     */
    warmup() {
      // Scope to the buy-box selectors — swap targets only ever come from
      // there. A page-wide scan also prefetched every cross-sell/featured
      // card's product (~20 extra api-v8 fetches per page load).
      const scope = document.getElementById('product-variant-selectors-new-pdp');
      const variants = (scope || document).querySelectorAll('product-variant[variant-value]');
      const urls = new Set();

      variants.forEach(v => {
        const url = v.getAttribute('variant-value');
        if (url) urls.add(url.split('?')[0]);
      });

      urls.forEach(url => {
        this._fetchAndStore(url);
      });
    }
  };

  // Currency utilities
  const CurrencyUtils = {
    getCurrencyCharacter() {
      switch (Shopify.currency.active) {
        case 'EUR':
          return '\u20AC';
        case 'GBP':
          return '\u00A3';
        case 'PLN':
          return 'z\u0142';
        case 'BGN':
          return '\u043B\u0432';
        case 'NOK':
        case 'SEK':
        case 'DKK':
          return 'kr';
        case 'CZK':
          return 'K\u010D';
        case 'HUF':
          return 'Ft';
        case 'CHF':
          return 'CHF';
        case 'CAD':
        case 'AUD':
        case 'NZD':
        case 'USD':
          return '$';
        default:
          // Unknown currency: prefer the storefront's own money format so the
          // JS-rendered price never silently mislabels a market's currency
          // (server-rendered Liquid prices already use this format).
          return (typeof Shopify !== 'undefined' &&
            typeof Shopify.currency?.active === 'string' &&
            Shopify.currency.active !== 'USD')
            ? Shopify.currency.active + ' '
            : '$';
      }
    },

    formatMoney(cents) {
      const currencySymbol = this.getCurrencyCharacter();
      const isHUF = Shopify.currency.active === 'HUF';

      let amount;
      if (isHUF) {
        amount = Math.round(cents).toString();
      } else {
        amount = (cents / 100).toFixed(2);
      }

      const europeanCurrencies = ['EUR', 'PLN', 'CZK', 'HUF', 'NOK', 'SEK', 'DKK', 'BGN'];
      const useEuropeanFormat = europeanCurrencies.includes(Shopify.currency.active);

      if (useEuropeanFormat) {
        if (!isHUF) {
          const parts = amount.split('.');
          if (parts[0].length > 3) {
            parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
          }
          amount = parts.join(',');
        } else {
          if (amount.length > 3) {
            amount = amount.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
          }
        }

        if (['\u004B\u010D', 'z\u0142', 'Ft', '\u043B\u0432'].includes(currencySymbol)) {
          return amount + ' ' + currencySymbol;
        }
      } else {
        if (amount.length > 3) {
          if (amount.includes('.')) {
            const parts = amount.split('.');
            parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            amount = parts.join('.');
          } else {
            amount = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
          }
        }
      }

      return currencySymbol + amount;
    },

    parsePrice(priceText) {
      const isHUF = Shopify.currency.active === 'HUF';

      if (isHUF) {
        return parseFloat(priceText.replace(/\./g, ''));
      } else if (priceText.includes(',')) {
        if (priceText.includes('.')) {
          return parseFloat(priceText.replace(/\./g, '').replace(',', '.'));
        } else {
          return parseFloat(priceText.replace(',', '.'));
        }
      } else {
        return parseFloat(priceText.replace(/,/g, ''));
      }
    }
  };

  // ============================================================================
  // DOM Selector Constants
  // ============================================================================

  const SELECTORS = {
    PRODUCT_FORM: 'product-form',
    PRODUCT_ID_INPUT: 'input[name="product-id"]',
    VARIANT_ID_INPUT: 'input[name="id"]',
    SUBSCRIPTION_RADIO: (productId) => `input[id="subscription-${productId}"]`,
    ONE_TIME_RADIO: (productId) => `input[id="one-time-${productId}"]`,
    PURCHASE_TYPE_RADIO: 'input[type="radio"][name="purchase_type"]:checked',
    ADD_TO_CART_BUTTON: 'button[name="add"]',
    PRICE_SPAN: '[data-price-id^="price-"]',
    STICKY_BUTTON: 'sticky-atc button[name="add"]',
    SUBSCRIPTION_OPTIONS: '.subscription-options',
    SUBSCRIPTION_SELECT: 'select[name="selling_plan"]',
    SELLING_PLAN_SELECT: 'select[id^="selling_plan_"]',
    BOTTLE_VARIANT: 'product-variant[data-packaging-type="bottle"].variant-selected-packaging',
    PACKAGING_VARIANT: '.variant-selected-packaging',
    RADIO_CONTAINER: '[data-radio-container]',
    FREEBIE_CONTAINER: (productId) => `#freebie-container-${productId}`,
    ONE_TIME_LABEL: (productId) => `label[for="one-time-${productId}"]`
  };

  // ============================================================================
  // Subscription Manager
  // ============================================================================

  class SubscriptionManager {
    static instances = {};

    constructor(productId, form = null) {
      this.productId = productId;
      this.form = form || document.querySelector('form[data-type="add-to-cart-form"]');
      this.subscriptionRadio = null;
      this.oneTimeRadio = null;
      this.init();
    }

    static getSubscriptionUIController(productId, form = null) {
      if (!SubscriptionManager.instances[productId]) {
        SubscriptionManager.instances[productId] = new SubscriptionManager(productId, form);
      }
      return SubscriptionManager.instances[productId];
    }

    static destroyInstance(productId) {
      if (SubscriptionManager.instances[productId]) {
        SubscriptionManager.instances[productId].cleanupEventListeners();
        delete SubscriptionManager.instances[productId];
      }
    }

    init() {
      this.subscriptionRadio = this.form?.querySelector(SELECTORS.SUBSCRIPTION_RADIO(this.productId));
      this.oneTimeRadio = this.form?.querySelector(SELECTORS.ONE_TIME_RADIO(this.productId));
    }

    updateProductId(newProductId) {
      this.productId = newProductId;
      this.init();
    }

    isSubscriptionSelected() {
      return this.subscriptionRadio?.checked || false;
    }

    getSelectedPurchaseType() {
      return this.isSubscriptionSelected() ? 'subscription' : 'one-time';
    }

    toggleSubscriptionOptions(showSubscription) {
      const benefitsContainer = document.getElementById(`subscription-benefits-${this.productId}`);
      if (benefitsContainer) {
        benefitsContainer.classList.toggle('hidden', !showSubscription);
      }

      const subscriptionOptions = this.form?.querySelector(SELECTORS.SUBSCRIPTION_OPTIONS);
      if (subscriptionOptions) {
        subscriptionOptions.classList.toggle('hidden', !showSubscription);
      }

      this.updateSellingPlanNames(showSubscription);

      const shopPayMessaging = document.getElementById('pdp-shop-pay-messaging');
      if (shopPayMessaging) {
        shopPayMessaging.style.display = showSubscription ? 'none' : 'block';
      }
    }

    updateSellingPlanNames(isSubscription) {
      const idPrefixSelect = this.form?.querySelector(SELECTORS.SELLING_PLAN_SELECT);
      if (idPrefixSelect) {
        idPrefixSelect.name = isSubscription ? 'selling_plan' : '';
      }

      const subscriptionOptions = this.form?.querySelector(SELECTORS.SUBSCRIPTION_OPTIONS);
      const subscriptionSelect = subscriptionOptions?.querySelector('select');
      if (subscriptionSelect) {
        subscriptionSelect.name = isSubscription ? 'selling_plan' : '';
      }
    }

    setupEventListeners(priceUpdateCallback = null) {
      if (this.subscriptionRadio && this.oneTimeRadio) {
        this.cleanupEventListeners();

        this.subscriptionChangeHandler = () => {
          this.toggleSubscriptionOptions(true);
          if (priceUpdateCallback) priceUpdateCallback();
        };

        this.oneTimeChangeHandler = () => {
          this.toggleSubscriptionOptions(false);
          if (priceUpdateCallback) priceUpdateCallback();
        };

        this.subscriptionRadio.addEventListener('change', this.subscriptionChangeHandler);
        this.oneTimeRadio.addEventListener('change', this.oneTimeChangeHandler);

        this.toggleSubscriptionOptions(this.isSubscriptionSelected());
      }
    }

    cleanupEventListeners() {
      if (this.subscriptionRadio && this.subscriptionChangeHandler) {
        this.subscriptionRadio.removeEventListener('change', this.subscriptionChangeHandler);
      }
      if (this.oneTimeRadio && this.oneTimeChangeHandler) {
        this.oneTimeRadio.removeEventListener('change', this.oneTimeChangeHandler);
      }
    }

    updateFreebieContainerVisibility() {
      const freebieContainer = document?.querySelector(SELECTORS.FREEBIE_CONTAINER(this.productId));
      if (!freebieContainer) return;

      // V8 mode: always hide standalone freebie container (freebies shown inline in benefits)
      if (document.querySelector('product-purchase-options-v8')) {
        freebieContainer.classList.add('hidden');
        return;
      }

      const hasFreebies = freebieContainer.children.length > 0;
      if (!hasFreebies) {
        freebieContainer.classList.add('hidden');
        return;
      }

      const isSubscriptionSelected = this.isSubscriptionSelected();
      const selectedPackaging = document.querySelector(SELECTORS.PACKAGING_VARIANT);
      const parentVariant = selectedPackaging?.closest('product-variant');
      let isBottleSelected = parentVariant?.getAttribute('data-packaging-type') === 'bottle';

      if (!isBottleSelected && window.currentProductTags) {
        isBottleSelected = window.currentProductTags.includes('bottle_container');
      }

      if ((isSubscriptionSelected || isBottleSelected) && hasFreebies) {
        freebieContainer.classList.remove('hidden');
      } else {
        freebieContainer.classList.add('hidden');
      }
    }

    selectSubscription() {
      if (this.subscriptionRadio && this.oneTimeRadio) {
        this.subscriptionRadio.checked = true;
        this.oneTimeRadio.checked = false;
        this.toggleSubscriptionOptions(true);
        this.subscriptionRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    cleanupSubscriptionSelectText() {
      const subscriptionSelect = this.form?.querySelector('.subscription-options');
      if (subscriptionSelect) {
        Array.from(subscriptionSelect.querySelectorAll('option')).forEach((option) => {
          const text = option.text;
          const deliveryText = text.split('-')[0].trim();
          option.text = deliveryText;
        });
      }
    }
  }

  // ============================================================================
  // TryNow Manager
  // ============================================================================

  class TryNowManager {
    static async hideTryNow(isSubscriptionSelected, retryCount = 0) {
      try {
        // Record the latest desired state so stale retries don't fight it.
        if (retryCount === 0) {
          TryNowManager._latestSub = isSubscriptionSelected;
        }

        const tryNowAppBlock = document.querySelector('trynow-cta-button')?.parentElement;

        // Retry in both cases until the TryNow block exists in the DOM.
        if (!tryNowAppBlock && retryCount < 10) {
          setTimeout(() => TryNowManager.hideTryNow(isSubscriptionSelected, retryCount + 1), 300);
          return;
        }

        if (!tryNowAppBlock) {
          return;
        }

        if (!window.trynow) {
          console.warn('TryNow script not loaded');
          return;
        }

        // A newer call superseded this one — abort.
        if (TryNowManager._latestSub !== isSubscriptionSelected) {
          return;
        }

        const tryNowToggle = tryNowAppBlock.querySelector('.tn-gate-toggle-input');
        const tryNowTextToggle = document.getElementById('trynow-toggle');

        let hasPassedGate = false;
        try {
          hasPassedGate = await window.trynow.hasPassedTryLink();
        } catch (trynowError) {
          console.warn('Error checking TryNow gate status:', trynowError);
        }

        if (isSubscriptionSelected) {
          tryNowAppBlock.style.display = 'none';
          // Keep the text toggle in sync with the button — hide it too.
          if (tryNowTextToggle) tryNowTextToggle.style.display = 'none';
          if (hasPassedGate && tryNowToggle?.checked) {
            tryNowTextToggle?.click();
          }
        } else {
          tryNowAppBlock.style.display = 'block';
          // Only show the toggle when the user actually crossed a TryLink.
          if (tryNowTextToggle && hasPassedGate) tryNowTextToggle.style.display = 'block';
          // Re-enable Try Before You Buy for one-time — unless the user explicitly
          // opted out ("No thanks, I'll buy without trying"). That choice persists
          // across product swaps via window._v8TryNowOptedOut.
          if (hasPassedGate && !tryNowToggle?.checked && !window._v8TryNowOptedOut) {
            tryNowTextToggle?.click();
          }
        }
      } catch (error) {
        console.error('Error in hideTryNow:', error);
      }
    }
  }

  // ============================================================================
  // DOM Update Manager
  // ============================================================================

  class DOMUpdateManager {
    constructor() {}

    updateProductMedia(doc, currentMedia) {
      const updatedMedia = doc.querySelector('product-media') || doc.querySelector('product-media-new-pdp');
      if (!updatedMedia || !currentMedia) return false;

      if (currentMedia.getAttribute('source') === 'featured-product') {
        const updatedImg = updatedMedia.querySelector('img');
        updatedImg.classList.remove('aspect-square', 'object-container');
        updatedImg.classList.add('aspect-[5/7]', 'object-cover');
        currentMedia.querySelector('img').replaceWith(updatedImg);
      } else {
        const upgradedMedia = currentMedia.cloneNode(false);
        currentMedia.replaceWith(updatedMedia);
        customElements.upgrade(upgradedMedia);
        if (upgradedMedia.generateCarousel) {
          upgradedMedia.generateCarousel();
        }
      }
      return true;
    }

    updateProductTitle(doc) {
      const updatedTitle = doc.querySelector('#productTitle');
      const currentTitle = document.querySelector('#productTitle');
      if (updatedTitle && currentTitle) {
        currentTitle.innerHTML = updatedTitle.innerHTML;
        return true;
      }
      return false;
    }

    updateBreadcrumbs(doc) {
      const updatedBreadCrumb = doc.querySelector('.breadcrumbs');
      const currentBreadcrumb = document.querySelector('.breadcrumbs');
      if (updatedBreadCrumb && currentBreadcrumb) {
        currentBreadcrumb.innerHTML = updatedBreadCrumb.innerHTML;
        return true;
      }
      return false;
    }

    updateContentSections(doc) {
      let updated = false;

      const updatedIngredientsSlider = doc.querySelector('#content-carousel');
      const currentIngredientsSlider = document.querySelector('#content-carousel');
      if (updatedIngredientsSlider && currentIngredientsSlider) {
        currentIngredientsSlider.innerHTML = updatedIngredientsSlider.innerHTML;
        updated = true;
      }

      const updatedFAQs = doc.querySelector('#editorial-faq');
      const currentFAQs = document.querySelector('#editorial-faq');
      if (updatedFAQs && currentFAQs) {
        currentFAQs.innerHTML = updatedFAQs.innerHTML;
        updated = true;
      }

      const updatedEditorialGalleries = doc.querySelector('#product-editorial-galleries');
      const currentEditorialGalleries = document.querySelector('#product-editorial-galleries');
      if (updatedEditorialGalleries && currentEditorialGalleries) {
        currentEditorialGalleries.innerHTML = updatedEditorialGalleries.innerHTML;
        const newGalleries = currentEditorialGalleries.querySelector('product-editorial-galleries');
        if (newGalleries) {
          customElements.upgrade(newGalleries);
        }
        updated = true;
      }

      return updated;
    }

    updateReviewWidget(doc, updatedProductId) {
      let reviewWidget = document.querySelector('.junip-product-review');
      let productSummaryStarWidget = document.querySelector('.junip-product-summary');

      if (!reviewWidget) return false;

      if (reviewWidget) {
        reviewWidget.setAttribute('data-product-id', updatedProductId.value);
      }

      if (productSummaryStarWidget) {
        productSummaryStarWidget.setAttribute('data-product-id', updatedProductId.value);
      }

      if (window.junip && window.junip.init) {
        window.junip.init();
      }

      return true;
    }

    updateTabGroup(doc) {
      const updatedInfo = doc.querySelector('tab-group');
      const currentInfo = document.querySelector('tab-group');
      if (updatedInfo && currentInfo) {
        currentInfo.replaceWith(updatedInfo);
        const upgradedInfo = document.querySelector('tab-group');
        customElements.upgrade(upgradedInfo);
        return true;
      }
      return false;
    }

    updateRecommendedProducts(doc) {
      const updatedProductRecs = doc.querySelector('recommended-products');
      const currentProductRecs = document.querySelector('recommended-products');
      if (updatedProductRecs && currentProductRecs) {
        currentProductRecs.innerHTML = updatedProductRecs.innerHTML;
        return true;
      }
      return false;
    }

    updateFreebieContainer(oldProductId, newProductId) {
      const freebieContainer = document.querySelector(`#freebie-container-${oldProductId}`);
      if (freebieContainer) {
        freebieContainer.id = `freebie-container-${newProductId}`;
        return true;
      }
      return false;
    }

    updateSimpleFaqGroup(doc) {
      const updatedFaqGroup = doc.querySelector('simple-faq-group');
      const currentFaqGroup = document.querySelector('simple-faq-group');
      if (updatedFaqGroup && currentFaqGroup) {
        currentFaqGroup.innerHTML = updatedFaqGroup.innerHTML;
        return true;
      }
      return false;
    }

    updateBeforeAfterSlider(doc) {
      const updatedSlider = doc.querySelector('section-before-after-slider');
      const currentSlider = document.querySelector('section-before-after-slider');
      if (updatedSlider && currentSlider) {
        if (currentSlider.state?.mainCarousel) {
          currentSlider.state.mainCarousel.destroy();
        }
        if (currentSlider.state?.thumbnailCarousel) {
          currentSlider.state.thumbnailCarousel.destroy();
        }

        currentSlider.initialized = false;
        currentSlider.classList.remove('slider-loaded');
        currentSlider.innerHTML = updatedSlider.innerHTML;

        if (currentSlider.connectedCallback) {
          currentSlider.connectedCallback();
        }

        return true;
      }
      return false;
    }
  }

  // ============================================================================
  // Price Manager
  // ============================================================================

  class PriceManager {
    constructor(productId, form = null) {
      this.productId = productId;
      this.form = form || document.querySelector('form[data-type="add-to-cart-form"]');
    }

    extractPriceFromLabel(productId = null) {
      const targetProductId = productId || this.productId;
      const oneTimeLabel = this.form?.querySelector(SELECTORS.ONE_TIME_LABEL(targetProductId));

      if (!oneTimeLabel) {
        console.warn(`One-time label not found for product ID: ${targetProductId}`);
        return null;
      }

      const priceSpan = oneTimeLabel.querySelector('.sr-only');
      let priceText = null;

      if (priceSpan) {
        const fullPriceMatch = priceSpan.textContent.match(/(?:\d+[\.,])*\d+(?:[\.,]\d+)?/);
        if (fullPriceMatch) {
          priceText = fullPriceMatch[0];
        } else {
          const priceMatch = priceSpan.textContent.match(/([^\d]*)(\d+(?:[.,]\d{2})?)/);
          if (priceMatch) priceText = priceMatch[2];
        }
      } else {
        const matches = [...oneTimeLabel.textContent.matchAll(/([^\d]*)(\d+(?:[.,]\d{2})?)/g)];
        const priceMatch = matches.length ? matches[matches.length - 1] : null;
        if (priceMatch) priceText = priceMatch[2];
      }

      if (!priceText) {
        console.warn('No price text found in label');
        return null;
      }

      return CurrencyUtils.parsePrice(priceText);
    }

    // Read the server-rendered discount % from the <product-purchase-options> element.
    // Liquid computes this from variant.selling_plan_allocations (SKIO group), so it's
    // the source of truth. Falls back to the legacy hardcoded dict, then to null
    // (caller treats null as "no discount, just show 'Subscribe & Save' / one-time price").
    getElementDiscount(productId) {
      const el = document.querySelector(`product-purchase-options[data-product-id="${productId}"]`)
              || document.getElementById(`purchase-options-${productId}`);
      const fromAttr = el?.getAttribute('data-discount-percent');
      if (fromAttr != null && fromAttr !== '') {
        const n = Number(fromAttr);
        if (!isNaN(n) && n > 0) return n;
      }
      return null;
    }

    calculateFinalPrice(basePrice, isSubscription, discountPercent = null) {
      if (!basePrice || isNaN(basePrice)) return null;

      const discount = discountPercent || this.getElementDiscount(this.productId) || window.subscriptionDiscounts?.[this.productId] || 0;
      const discountMultiplier = (100 - discount) / 100;
      return isSubscription ? basePrice * discountMultiplier : basePrice;
    }

    formatPriceForDisplay(price) {
      const isHUF = Shopify.currency.active === 'HUF';
      return isHUF ? CurrencyUtils.formatMoney(price) : CurrencyUtils.formatMoney(price * 100);
    }

    updateButtonPrice(button, formattedPrice, buttonText = 'Add to bag', compareFormatted = null) {
      const priceSpan = button?.querySelector(SELECTORS.PRICE_SPAN);
      if (!priceSpan) {
        console.warn('Price span not found in button');
        return false;
      }

      const priceId = priceSpan.getAttribute('data-price-id');
      // When a compare price is passed (subscribe selected w/ a real discount), render the
      // one-time price with line-through next to the discounted subscribe price. Matches the
      // same line-through styling used by `card-atc.liquid` for compare_at_price.
      const priceMarkup = compareFormatted && compareFormatted !== formattedPrice
        ? `<span class="line-through text-brand-secondary-350">${compareFormatted}</span>&nbsp;${formattedPrice}`
        : formattedPrice;
      button.innerHTML = `
        ${buttonText}&nbsp;\u2014&nbsp;
        <span data-price-id="${priceId}">${priceMarkup}</span>
      `;
      return true;
    }

    updateStickyButton(formattedPrice) {
      const stickyButton = document.querySelector(SELECTORS.STICKY_BUTTON);
      if (stickyButton) {
        const stickyPriceSpan = stickyButton.querySelector(SELECTORS.PRICE_SPAN);
        if (stickyPriceSpan) {
          stickyPriceSpan.textContent = formattedPrice;
          return true;
        }
      }
      return false;
    }

    shouldSkipPriceUpdate(isSubscription, forceUpdate = false) {
      const bottleVariant = document.querySelector(SELECTORS.BOTTLE_VARIANT);
      const isBottleProduct = !!bottleVariant || window.location.pathname.includes('bottle');
      return isBottleProduct && !isSubscription && !forceUpdate;
    }

    updateAllPrices(isSubscription, forceUpdate = false, productId = null) {
      try {
        const targetProductId = productId || this.productId;

        if (this.shouldSkipPriceUpdate(isSubscription, forceUpdate)) {
          return { success: true, skipped: true };
        }

        const basePrice = this.extractPriceFromLabel(targetProductId);
        if (!basePrice) {
          return { success: false, error: 'Could not extract base price' };
        }

        const finalPrice = this.calculateFinalPrice(basePrice, isSubscription);
        if (!finalPrice) {
          return { success: false, error: 'Could not calculate final price' };
        }

        const formattedPrice = this.formatPriceForDisplay(finalPrice);
        // When subscribed at a real discount, also pass the one-time (base) price so the
        // button can render it with line-through next to the discounted price.
        const compareFormatted = isSubscription && finalPrice < basePrice
          ? this.formatPriceForDisplay(basePrice)
          : null;

        const mainButton = this.form?.querySelector(SELECTORS.ADD_TO_CART_BUTTON);
        const buttonLabel = isSubscription ? 'Subscribe & Save' : 'Add to bag';
        const buttonUpdated = this.updateButtonPrice(mainButton, formattedPrice, buttonLabel, compareFormatted);
        const stickyUpdated = this.updateStickyButton(formattedPrice);

        return {
          success: true,
          formattedPrice,
          buttonUpdated,
          stickyUpdated,
          basePrice,
          finalPrice
        };
      } catch (error) {
        console.error('Error in updateAllPrices:', error);
        return { success: false, error: error.message };
      }
    }
  }

  // ============================================================================
  // Custom Elements
  // ============================================================================

  // ProductVariant - Simple element for variant options
  if (!customElements.get('product-variant')) {
    customElements.define(
      'product-variant',
      class ProductVariant extends HTMLElement {
        constructor() {
          super();
        }
      }
    );
  }

  // VariantSelect - Main variant selection component
  customElements.define(
    'variant-select',
    class VariantSelect extends HTMLElement {
      hasInitialized = false;

      constructor() {
        super();

        this.productForm = this.closest('product-form');
        this.productVariants = this.querySelectorAll('product-variant');
        this.selectedVariantOption = this.querySelectorAll('input[name="selected-variant-option"]');
        this.selectedVariantId = this.querySelectorAll('input[name="selected-variant-id"]');
        this.globalProductId = this.productForm?.querySelector(SELECTORS.PRODUCT_ID_INPUT)?.value;

        this.subscriptionManager = null;
        this.priceManager = null;
        this.domUpdateManager = new DOMUpdateManager();
      }

      connectedCallback() {
        if (this.hasInitialized) return;
        this.hasInitialized = true;

        this.initializeVariantSelection();
        this.initializePackagingSelection();
        this.applyEventListeners();
        this.applySubscriptionUI();

        // V8: pre-fetch all linked product API templates in background
        if (document.querySelector('.v8-size-card') || document.querySelector('.v8-formula-card')) {
          V8ProductCache.warmup();
        }
      }

      initializeVariantSelection() {
        const selectedSizeVariant = this.querySelector('product-variant.variant-selected-size');
        if (!selectedSizeVariant) return;

        const variantUrl = selectedSizeVariant.getAttribute('variant-value');
        const selectedSizeInput = this.querySelector('#selected-size');

        if (selectedSizeInput && variantUrl) {
          const currentValue = selectedSizeInput.value;
          const hasSize = /(\d+-oz|\d+oz|\d+ml|d+-ml|\d+pack|d+-pack)/.test(currentValue);
          if (!hasSize) {
            selectedSizeInput.value = variantUrl;
          }
        }
      }

      initializePackagingSelection() {
        const hasPackagingVariants = [...this.productVariants].some((variant) =>
          variant.hasAttribute('data-packaging-type')
        );

        if (!hasPackagingVariants) {
          return;
        }

        const productTags = window.currentProductTags || [];
        let currentPackagingType = null;

        if (productTags.includes('bottle_container')) {
          currentPackagingType = 'bottle';
        } else if (productTags.includes('pouch_container')) {
          currentPackagingType = 'pouch';
        }

        [...this.productVariants].forEach((variant) =>
          variant.classList.remove('variant-selected-packaging')
        );

        this.productVariants.forEach((variant) => {
          const packagingType = variant.getAttribute('data-packaging-type');
          if (packagingType === currentPackagingType) {
            variant.classList.add('variant-selected-packaging');
          }
        });
      }

      applyEventListeners() {
        [...this.productVariants].forEach((variant) => {
          variant.addEventListener('click', this.onVariantChange.bind(this));
        });
      }

      initializeManagers(productId = null, form = null) {
        const targetForm = form || this.productForm?.querySelector('form') || this.productForm;
        const productIdInput = targetForm?.querySelector(SELECTORS.PRODUCT_ID_INPUT);
        const targetProductId = productId || productIdInput?.value;

        if (!targetProductId) {
          console.warn('No product ID found for manager initialization');
          return null;
        }

        if (!this.subscriptionManager || this.subscriptionManager.productId !== targetProductId) {
          this.subscriptionManager = SubscriptionManager.getSubscriptionUIController(targetProductId, targetForm);
          this.priceManager = new PriceManager(targetProductId, targetForm);
        }

        return { productId: targetProductId, form: targetForm };
      }

      applySubscriptionUI(productId = null) {
        const result = this.initializeManagers(productId);
        if (!result) return;

        this.subscriptionManager.cleanupSubscriptionSelectText();

        this.subscriptionManager.setupEventListeners(() => {
          this.updateAddToCartPrice(result.productId);
          this.subscriptionManager.updateFreebieContainerVisibility();
        });

        this.updateAddToCartPrice(result.productId);
        this.subscriptionManager.updateFreebieContainerVisibility();
      }

      updateAddToCartPrice = (productId = '', forceUpdate = false) => {
        try {
          // V8 handles its own prices via product-purchase-options-v8.updateATCPrice()
          if (document.querySelector('product-purchase-options-v8')) return;

          const isBottleProduct = document.querySelector(SELECTORS.BOTTLE_VARIANT);
          const hasSubscriptionUI = !!document.querySelector('label[for^="one-time-"]');

          if (isBottleProduct && !hasSubscriptionUI && !forceUpdate) {
            console.log('Skipping price update for bottle product without subscription UI');
            return;
          }

          const result = this.initializeManagers(productId);
          if (!result) {
            console.warn('Failed to initialize managers in updateAddToCartPrice');
            return;
          }

          const isSubscriptionSelected = this.subscriptionManager?.isSubscriptionSelected() || false;

          const priceResult = this.priceManager.updateAllPrices(
            isSubscriptionSelected,
            forceUpdate,
            result.productId
          );

          if (!priceResult.success && !priceResult.skipped) {
            console.warn('Price update failed:', priceResult.error);
            return;
          }

          TryNowManager.hideTryNow(isSubscriptionSelected);
        } catch (error) {
          console.error('Error in updateAddToCartPrice:', error);
        }
      };

      /**
       * Pre-mutate a fetched V8 purchase-options element to reflect one-time state
       * before it's inserted into the DOM, preventing visual flicker.
       */
      async renderProductInformation(originalTargetValue) {
        let targetValue = originalTargetValue;
        try {
          // V8: Fetch from lightweight API template instead of full page.
          // Detect V8 by the buy-box wrapper, NOT the purchase-options element —
          // products without selling plans (accessories, brushes) have no
          // purchase options but still swap via the api-v8 fragment. Falling
          // into the else-branch would fetch the full ~1MB page per click.
          const isV8 =
            !!document.querySelector('.v8-product-info-wrapper') ||
            !!document.querySelector('product-purchase-options-v8');
          let fetchUrl = targetValue;
          let data;

          if (isV8) {
            // Single template — always api-v8. Purchase type state is
            // pre-applied to the parsed fragment before DOM insertion.
            const cached = V8ProductCache.get(targetValue);
            if (cached) {
              data = cached;
            } else {
              data = await V8ProductCache.getOrFetch(targetValue);
            }

            if (!data) {
              // Final fallback: direct fetch
              const separator = targetValue.includes('?') ? '&' : '?';
              fetchUrl = appendCountryParam(targetValue + separator + 'view=api-v8');
              const response = await fetch(fetchUrl);
              if (!response.ok) throw new Error('Network response was not ok');
              data = await response.text();
            }
          } else {
            const response = await fetch(appendCountryParam(fetchUrl));
            if (!response.ok) throw new Error('Network response was not ok');
            data = await response.text();
          }

          const parser = new DOMParser();
          let doc = parser.parseFromString(data, 'text/html');

          // V8 Formula swap: carry over the previously selected size.
          // The formula card URL points to the default product (bottle).
          // If the user had a different size selected (e.g. 20oz Refill),
          // find that size in the fetched doc's size grid, fetch its product
          // from cache, and use that as our doc — single DOM update, no flicker.
          if (isV8 && window._v8PendingSizeName) {
            const pendingSize = window._v8PendingSizeName;
            window._v8PendingSizeName = null;
            const newSizeCards = doc.querySelectorAll('.v8-size-card');
            let matchingCard = null;
            newSizeCards.forEach((card) => {
              const nameEl = card.querySelector('.v8-size-name');
              if (nameEl && nameEl.textContent.trim() === pendingSize) {
                matchingCard = card;
              }
            });
            if (matchingCard && matchingCard.getAttribute('data-selected') !== 'true') {
              const sizeUrl = matchingCard.getAttribute('variant-value');
              // Skip the size restore when the matching card's URL is the
              // product we're leaving (e.g. Air Dry Kit: its only size,
              // "4 Product Set", points back at the Original kit in every
              // formula's grid). Otherwise the swap runs end to end against
              // the old product and the formula switch silently reverts.
              const leavingPath = window.location.pathname;
              if (sizeUrl && sizeUrl.split('?')[0] !== leavingPath) {
                // Update data-selected in the doc before we potentially re-parse
                newSizeCards.forEach((c) => c.setAttribute('data-selected', 'false'));
                matchingCard.setAttribute('data-selected', 'true');
                // Update hidden input
                const sizeInput = doc.querySelector('#selected-size');
                if (sizeInput) sizeInput.value = sizeUrl;
                const sizeIdInput = matchingCard.closest('variant-select')?.querySelector('input[name="selected-variant-id"]');
                if (sizeIdInput) sizeIdInput.value = matchingCard.getAttribute('variant-id') || '';

                // Fetch the matching size product content and re-parse
                let sizeData = V8ProductCache.get(sizeUrl);
                if (!sizeData) {
                  sizeData = await V8ProductCache.getOrFetch(sizeUrl);
                }
                if (!sizeData) {
                  const sep = sizeUrl.includes('?') ? '&' : '?';
                  const resp = await fetch(appendCountryParam(sizeUrl + sep + 'view=api-v8'));
                  if (resp.ok) sizeData = await resp.text();
                }
                if (sizeData) {
                  // Re-parse with the correct product, but preserve the formula
                  // selector + size grid from the original doc (they're the same
                  // across sizes within a formula).
                  const sizeDoc = parser.parseFromString(sizeData, 'text/html');
                  // Carry over the size selection state to the new doc
                  const sizeSizeCards = sizeDoc.querySelectorAll('.v8-size-card');
                  sizeSizeCards.forEach((c) => c.setAttribute('data-selected', 'false'));
                  sizeSizeCards.forEach((c) => {
                    const nameEl = c.querySelector('.v8-size-name');
                    if (nameEl && nameEl.textContent.trim() === pendingSize) {
                      c.setAttribute('data-selected', 'true');
                    }
                  });
                  const sizeSizeInput = sizeDoc.querySelector('#selected-size');
                  if (sizeSizeInput) sizeSizeInput.value = sizeUrl;

                  doc = sizeDoc;
                  targetValue = sizeUrl;
                  data = sizeData;
                }
              }
            }
          }

          if (window.location.pathname.includes('/products')) {
            history.pushState({}, '', targetValue);
          }

          // V8 Pre-apply: Mutate the parsed fragment to reflect the current
          // purchase type BEFORE any innerHTML swaps touch the live DOM.
          // The api-v8 template renders one-time (OTP) as the default visual state.
          // Pre-apply the current purchase type to the parsed fragment BEFORE
          // injecting into the live DOM — prevents any price/state flicker.
          if (isV8) {
            const purchaseType = window._v8PurchaseType || 'subscription';
            const isSub = purchaseType === 'subscription';

            const fragPurchaseOpts = doc.querySelector('product-purchase-options-v8');
            const fragSelectedCard = doc.querySelector('.v8-size-card[data-selected="true"]');
            const fragNoSubscription = fragSelectedCard?.getAttribute('data-has-subscription') === 'false';

            // Force one-time if the selected product has no subscription offer
            const effectiveIsSub = fragNoSubscription ? false : isSub;

            // 1. Pre-apply size grid prices
            // Template renders OTP (base) prices. When subscription is active,
            // rewrite every size card to show subscription prices.
            if (effectiveIsSub) {
              // Prefer the server-computed % from the fragment's data attribute; fall back to legacy dict.
              const fragDiscountAttr = fragPurchaseOpts?.getAttribute('data-discount-percent');
              const fragDiscount = fragDiscountAttr ? Number(fragDiscountAttr) : 0;
              const discount = (fragDiscount > 0 ? fragDiscount : 0) || window.subscriptionDiscounts?.[fragPurchaseOpts?.getAttribute('data-product-id')] || 0;
              doc.querySelectorAll('.v8-size-card').forEach((card) => {
                const cardSubPrice = parseInt(card.getAttribute('data-sub-price')) || 0;
                const cardBasePrice = parseInt(card.getAttribute('data-price')) || 0;
                const cardWashes = parseInt(card.getAttribute('data-washes')) || 24;
                const displayPrice = cardSubPrice || cardBasePrice;
                const priceEl = card.querySelector('.v8-size-price');
                const perWashEl = card.querySelector('.v8-size-per-wash');
                if (priceEl) priceEl.textContent = CurrencyUtils.formatMoney(displayPrice);
                if (perWashEl && cardWashes) {
                  perWashEl.textContent = '~' + CurrencyUtils.formatMoney(Math.floor(displayPrice / cardWashes)) + '/wash';
                }
              });
            }
            // OTP is default — size grid prices already correct from template

            // 2. Pre-apply purchase options card state
            if (fragPurchaseOpts) {
              const fragSubCard = fragPurchaseOpts.querySelector('[data-purchase-card="subscription"]');
              const fragOtpCard = fragPurchaseOpts.querySelector('[data-purchase-card="one-time"]');
              if (fragSubCard) fragSubCard.setAttribute('data-active', String(effectiveIsSub));
              if (fragOtpCard) fragOtpCard.setAttribute('data-active', String(!effectiveIsSub));

              // Radio visual states
              const fragSubRadio = fragPurchaseOpts.querySelector('[data-radio-visual="subscription"]');
              const fragOtpRadio = fragPurchaseOpts.querySelector('[data-radio-visual="one-time"]');
              if (fragSubRadio) fragSubRadio.setAttribute('data-active', String(effectiveIsSub));
              if (fragOtpRadio) fragOtpRadio.setAttribute('data-active', String(!effectiveIsSub));

              // Subscription details visibility
              const fragSubDetails = fragPurchaseOpts.querySelector('.v8-sub-details');
              if (fragSubDetails) fragSubDetails.setAttribute('data-visible', String(effectiveIsSub));

              // Hidden radio inputs
              const fragSubInput = fragPurchaseOpts.querySelector('input[value="subscription"]');
              const fragOtpInput = fragPurchaseOpts.querySelector('input[value="one-time"]');
              if (fragSubInput) fragSubInput.checked = effectiveIsSub;
              if (fragOtpInput) fragOtpInput.checked = !effectiveIsSub;

              // Selling plan name
              const fragSellingPlan = fragPurchaseOpts.querySelector('select[data-subscription-select]');
              if (fragSellingPlan) fragSellingPlan.name = effectiveIsSub ? 'selling_plan' : '';

              // 3. Pre-apply purchase options price displays
              const subPrice = parseInt(fragPurchaseOpts.getAttribute('data-sub-price')) || 0;
              const basePrice = parseInt(fragPurchaseOpts.getAttribute('data-base-price')) || 0;
              const washes = parseInt(fragPurchaseOpts.getAttribute('data-washes')) || 24;

              // Subscribe card price — template renders sub price from Liquid, should be correct
              // Compare price visibility
              const subComparePriceEl = fragPurchaseOpts.querySelector('.v8-sub-compare-price');
              if (subComparePriceEl) {
                subComparePriceEl.style.display = subPrice < basePrice ? '' : 'none';
              }

              // 4. Pre-apply ATC button price and label
              const finalPrice = effectiveIsSub ? subPrice : basePrice;
              doc.querySelectorAll('[data-price-id^="price-"]').forEach((span) => {
                span.textContent = CurrencyUtils.formatMoney(finalPrice);
              });
              const fragAtcBtn = doc.querySelector('#product-atc button[type="submit"]');
              if (fragAtcBtn) {
                const buttonLabel = effectiveIsSub ? 'Subscribe & Save' : 'Add to bag';
                const priceSpan = fragAtcBtn.querySelector('[data-price-id]');
                const priceHtml = priceSpan ? priceSpan.outerHTML : CurrencyUtils.formatMoney(finalPrice);
                fragAtcBtn.innerHTML = buttonLabel + ' \u2014 \n      ' + priceHtml;
              }
              updateRivoEarnPoints(doc.querySelector('#product-atc'), finalPrice, effectiveIsSub);
            }
          }

          const scriptContent = data.match(/window\.currentProductTags\s*=\s*(\[.*?\]);/);
          if (scriptContent && scriptContent[1]) {
            try {
              window.currentProductTags = JSON.parse(scriptContent[1]);
            } catch (e) {
              console.warn('Failed to parse product tags:', e);
            }
          }

          const updatedForm = doc.querySelector('#product-atc');
          const updatedProductId = doc.querySelector('input[name="product-id"]');
          const updatedVariantSelectorsNewPdp = doc.querySelector('#product-variant-selectors-new-pdp');
          const updatedSubscriptionOptionsNewPdp = doc.querySelector('[data-subscription-select]');

          if (updatedProductId) {
            const currentProductId = this.productForm.querySelector('input[name="product-id"]');
            if (currentProductId && currentProductId.value !== updatedProductId.value) {
              const oldProductId = currentProductId.value;
              const newProductId = updatedProductId.value;

              this.domUpdateManager.updateFreebieContainer(oldProductId, newProductId);
              this.domUpdateManager.updateReviewWidget(doc, updatedProductId);
            }
          }

          this.domUpdateManager.updateContentSections(doc);
          this.domUpdateManager.updateBreadcrumbs(doc);
          this.domUpdateManager.updateSimpleFaqGroup(doc);
          this.domUpdateManager.updateBeforeAfterSlider(doc);

          const currentMedia =
            document.querySelector('product-media') || document.querySelector('product-media-new-pdp');
          if (!this.domUpdateManager.updateProductMedia(doc, currentMedia)) {
            console.error('Updated media section not found in the response');
          }

          this.domUpdateManager.updateProductTitle(doc);

          if (updatedForm) {
            const currentForm = document.querySelector('#product-atc');
            const currentVariantSelectorsNewPdp = document.querySelector('#product-variant-selectors-new-pdp');
            const currentSubscriptionOptionsNewPdp = document.querySelector('[data-subscription-select]');

            const updatedFullForm = doc.querySelector('product-form form');
            const currentFullForm = document.querySelector('product-form form');

            const purchaseTypeLabel = currentFullForm?.querySelector('#purchase-type-label');

            const updatedPurchaseOptions = updatedFullForm?.querySelector('.purchase-options');
            const currentPurchaseOptions = currentFullForm?.querySelector('.purchase-options');

            if (!updatedPurchaseOptions && currentPurchaseOptions) {
              currentPurchaseOptions.classList.add('hidden');
              if (purchaseTypeLabel) {
                purchaseTypeLabel.classList.add('hidden');
              }

              const oneTimeRadio = currentFullForm.querySelector(`input[id^="one-time-"]`);

              if (oneTimeRadio && !oneTimeRadio.checked) {
                oneTimeRadio.checked = true;
                oneTimeRadio.dispatchEvent(new Event('change', { bubbles: true }));
              }
            } else if (updatedPurchaseOptions && !currentPurchaseOptions) {
              let werePurchaseOptionsInserted = false;

              werePurchaseOptionsInserted = this.insertPurchaseOptions(updatedFullForm, currentFullForm);

              if (werePurchaseOptionsInserted) {
                const variantSelect = document.querySelector('variant-select');
                if (variantSelect) {
                  variantSelect.applySubscriptionUI();
                }
              }
            } else if (updatedPurchaseOptions && currentPurchaseOptions) {
              currentPurchaseOptions.classList.remove('hidden');
              if (purchaseTypeLabel) {
                purchaseTypeLabel.classList.remove('hidden');
              }
            }

            if (!currentForm.querySelector('sticky-atc')) {
              updatedForm.querySelector('sticky-atc')?.remove();
            }

            if (currentForm.dataset.externalVariantSelectors === 'true') {
              updatedForm.querySelectorAll('variant-select').forEach((el) => el.remove());
            }

            currentForm.innerHTML = updatedForm.innerHTML;

            if (updatedVariantSelectorsNewPdp && currentVariantSelectorsNewPdp) {
              currentVariantSelectorsNewPdp.innerHTML = updatedVariantSelectorsNewPdp.innerHTML;
            }

            if (updatedSubscriptionOptionsNewPdp) {
              if (currentSubscriptionOptionsNewPdp) {
                currentSubscriptionOptionsNewPdp.innerHTML = updatedSubscriptionOptionsNewPdp.innerHTML;
              }
            }

            // V8: Replace purchase-options-v8 inner content.
            // State was already pre-applied to `doc` fragment above — zero flicker.
            const updatedV8PurchaseOptions = doc.querySelector('product-purchase-options-v8');
            const currentV8PurchaseOptions = document.querySelector('product-purchase-options-v8');
            if (updatedV8PurchaseOptions && currentV8PurchaseOptions) {
              currentV8PurchaseOptions.innerHTML = updatedV8PurchaseOptions.innerHTML;

              // Sync ALL data-* attributes automatically
              [...updatedV8PurchaseOptions.attributes]
                .filter(a => a.name.startsWith('data-'))
                .forEach(a => currentV8PurchaseOptions.setAttribute(a.name, a.value));
            }

            // V8: Re-warm the cache after variant selectors are swapped
            // (formula switch brings new size cards with new product URLs)
            if (document.querySelector('.v8-size-card, .v8-formula-card')) {
              V8ProductCache.warmup();
            }

            const checkForSubscriptionUI = setInterval(() => {
              const bulletsContainer = document.querySelector('.subscription-bullets-container');

              if (bulletsContainer && window.updateSubscriptionBullets) {
                clearInterval(checkForSubscriptionUI);
                window.updateSubscriptionBullets(updatedProductId.value);
              }
            }, 100);

            setTimeout(() => clearInterval(checkForSubscriptionUI), 5000);

            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = data;
            const newProductId = updatedProductId.value;

            const currentRadio = document.querySelector(`input[type="radio"][name="purchase_type"]:checked`);
            const wasSubscriptionSelected = currentRadio?.value === 'subscription';

            const currentOneTimeLabel = document.querySelector('label[for^="one-time-"]');
            const newOneTimeLabel = tempDiv.querySelector(`label[for="one-time-${newProductId}"]`);

            if (currentOneTimeLabel && newOneTimeLabel) {
              const srOnlySpan = newOneTimeLabel.querySelector('.sr-only');
              if (srOnlySpan) {
                const fullPriceText = srOnlySpan.textContent;
                currentOneTimeLabel.innerHTML = `One-time purchase <span class="sr-only">${fullPriceText}</span>`;
              }
            }

            const newSubscriptionSelect = tempDiv.querySelector(`#selling_plan_${newProductId}`);

            if (newSubscriptionSelect) {
              const currentSelect = document.querySelector('select[id^="selling_plan_"]');

              if (currentSelect) {
                const updatedSelect = document.createElement('select');
                updatedSelect.id = `selling_plan_${newProductId}`;
                updatedSelect.className = currentSelect.className;
                updatedSelect.name = wasSubscriptionSelected ? 'selling_plan' : '';

                const options = Array.from(newSubscriptionSelect.options);

                const modifiedOptions = options.map((option) => {
                  const text = option.text;
                  const deliveryText = text.split('-')[0].trim();

                  const newOption = document.createElement('option');
                  newOption.value = option.value;
                  newOption.text = deliveryText;
                  return newOption;
                });

                modifiedOptions.forEach((option) => updatedSelect.appendChild(option));
                currentSelect.parentNode.replaceChild(updatedSelect, currentSelect);
              }
            }

            const updatedAddToCartForm = doc.querySelector('form[data-type="add-to-cart-form"]');

            currentForm.innerHTML = updatedForm.innerHTML;

            const newCurrentAddToCartForm = document.querySelector('form[data-type="add-to-cart-form"]');
            if (
              updatedAddToCartForm?.getAttribute('id') &&
              updatedAddToCartForm.getAttribute('id') !== newCurrentAddToCartForm?.getAttribute('id')
            ) {
              newCurrentAddToCartForm?.setAttribute('id', updatedAddToCartForm.getAttribute('id'));
            }

            const currentProductIdElement = this.productForm.querySelector('input[name="product-id"]');
            const oldProductId = currentProductIdElement?.value || this.globalProductId;
            const updatedProductIdValue = updatedProductId.value;

            if (oldProductId && updatedProductIdValue && oldProductId !== updatedProductIdValue) {
              SubscriptionManager.destroyInstance(oldProductId);

              const oldRadios = document.querySelectorAll(`[id*="${oldProductId}"]`);
              oldRadios.forEach((element) => {
                const oldId = element.id;
                const newId = oldId.replace(oldProductId, updatedProductIdValue);
                element.id = newId;
              });

              const oldLabels = document.querySelectorAll(`[for*="${oldProductId}"]`);
              oldLabels.forEach((element) => {
                const oldFor = element.getAttribute('for');
                const newFor = oldFor.replace(oldProductId, updatedProductIdValue);
                element.setAttribute('for', newFor);
              });
            }

            // Skip legacy subscription label updates in V8 mode
            if (!document.querySelector('product-purchase-options-v8')) {
              // Prefer the server-computed % from the swapped-in <product-purchase-options>
              // element's data-discount-percent attr; fall back to legacy dict. If neither has
              // a real value, render "Subscribe & Save" with no % rather than lying with "5%".
              const elDiscount = this.getElementDiscount
                ? this.getElementDiscount(updatedProductIdValue)
                : (() => {
                    const el = document.querySelector(`product-purchase-options[data-product-id="${updatedProductIdValue}"]`);
                    const v = el?.getAttribute('data-discount-percent');
                    const n = v != null ? Number(v) : NaN;
                    return !isNaN(n) && n > 0 ? n : null;
                  })();
              const discountPercent = elDiscount || window.subscriptionDiscounts?.[updatedProductIdValue] || 0;
              const labelHtml = discountPercent > 0 ? `Subscribe & Save ${discountPercent}%` : 'Subscribe & Save';
              const subscriptionLabel = document.querySelector(`label[for="subscription-${updatedProductIdValue}"]`);

              if (subscriptionLabel) {
                subscriptionLabel.innerHTML = labelHtml;
              } else {
                const oldSubscriptionLabel = document.querySelector(`label[for^="subscription-"]`);
                if (oldSubscriptionLabel) {
                  oldSubscriptionLabel.innerHTML = labelHtml;
                }
              }

              const subscriptionRadio = document.getElementById(`subscription-${updatedProductIdValue}`);
              const oneTimeRadio = document.getElementById(`one-time-${updatedProductIdValue}`);

              if (subscriptionRadio && oneTimeRadio) {
                this.updateAddToCartPrice(updatedProductId.value);
              }
            }
          }

          this.domUpdateManager.updateTabGroup(doc);
          this.domUpdateManager.updateRecommendedProducts(doc);

          if (updatedProductId) {
            this.domUpdateManager.updateReviewWidget(doc, updatedProductId);
          }

          if (updatedProductId) {
            const currentProductId = this.productForm.querySelector('input[name="product-id"]');

            if (currentProductId) {
              const oldProductId = currentProductId.value;
              currentProductId.value = updatedProductId.value;

              window.dispatchEvent(
                new CustomEvent('product-id-changed', {
                  detail: {
                    oldProductId: oldProductId,
                    newProductId: updatedProductId.value
                  }
                })
              );
            }
          }

          window.dispatchEvent(new CustomEvent('recalculate-try-now-state'));

          setTimeout(() => {
            const currentRadio = document.querySelector(`input[type="radio"][name="purchase_type"]:checked`);
            const wasSubscriptionSelected = currentRadio?.value === 'subscription';

            const updatedProductId = document.querySelector('input[name="product-id"]')?.value;

            // V8 mode: reinitialize purchase options component
            // State is preserved via window._v8PurchaseType (set by setActiveCard)
            const v8PurchaseOptions = document.querySelector('product-purchase-options-v8');
            if (v8PurchaseOptions && updatedProductId) {
              v8PurchaseOptions.init(updatedProductId);

              if (window.switchingToBottle) {
                window.switchingToBottle = false;
              }
              return;
            }

            // Legacy mode: restore subscription radio/select state
            const newRadios = document.querySelectorAll('input[data-purchase-type-radio]');
            if (newRadios.length > 0) {
              newRadios.forEach((radio) => {
                if (
                  (radio.value === 'subscription' && wasSubscriptionSelected) ||
                  (radio.value === 'one-time' && !wasSubscriptionSelected)
                ) {
                  radio.checked = true;
                  radio.dispatchEvent(new Event('change', { bubbles: true }));
                }
              });

              if (window.pendingSubscriptionSelection) {
                const currentUrl = new URL(window.location.href);
                this.onPouchClick(currentUrl);
                window.pendingSubscriptionSelection = false;
              } else if (!wasSubscriptionSelected) {
                const form = newRadios[0]?.closest('form');
                if (form) {
                  const idPrefixSelect = form.querySelector('select[id^="selling_plan_"]');
                  if (idPrefixSelect) {
                    idPrefixSelect.name = '';
                  }

                  const subscriptionOptions = form.querySelector('.subscription-options');
                  const subscriptionSelect = subscriptionOptions?.querySelector('select');
                  if (subscriptionSelect) {
                    subscriptionSelect.name = '';
                  }
                }
              }
            }

            const variantSelect = document.querySelector('variant-select');
            if (variantSelect && updatedProductId) {
              if (window.switchingToBottle) {
                window.switchingToBottle = false;
                return;
              }

              this.applySubscriptionUI(updatedProductId.value);
              this.updateAddToCartPrice(updatedProductId.value, false);
            }
          }, 100);
        } catch (error) {
          console.error('Error fetching & updated section:', error);
        } finally {
          setTimeout(() => {
            window.isVariantSwitching = false;
          }, 200);
        }
      }

      insertPurchaseOptions(updatedFullForm, currentFullForm) {
        const updatedPurchaseOptions = updatedFullForm?.querySelector('.purchase-options');
        const updatedPurchaseTypeLabel = updatedFullForm?.querySelector('#purchase-type-label');
        const productAtcDiv = currentFullForm.querySelector('#product-atc');

        if (!productAtcDiv || !updatedPurchaseOptions || !updatedPurchaseTypeLabel) {
          return false;
        }

        const labelClone = updatedPurchaseTypeLabel.cloneNode(true);
        productAtcDiv.parentNode.insertBefore(labelClone, productAtcDiv);

        const purchaseOptionsClone = updatedPurchaseOptions.cloneNode(true);

        const subscriptionOptions = purchaseOptionsClone.querySelector('.subscription-options');
        const subscriptionBenefits = subscriptionOptions?.querySelector('.subscription-benefits');
        const subscriptionContainer = purchaseOptionsClone
          .querySelector('input[value="subscription"]')
          ?.closest('[data-radio-container]');

        if (subscriptionContainer) {
          subscriptionContainer.classList.remove('bg-brand-secondary-400');
          subscriptionContainer.classList.add('bg-white');
        }

        if (subscriptionOptions) {
          subscriptionOptions.classList.remove('hidden');
        }

        if (subscriptionBenefits) {
          subscriptionBenefits.classList.remove('hidden');
        }

        productAtcDiv.parentNode.insertBefore(purchaseOptionsClone, productAtcDiv);

        return true;
      }

      onPouchClick(currentUrl) {
        const currentProductId = document.querySelector('input[name="product-id"]')?.value;

        if (currentProductId && this.subscriptionManager?.productId !== currentProductId) {
          this.subscriptionManager.updateProductId(currentProductId);
        }

        if (this.subscriptionManager) {
          this.subscriptionManager.init();
          this.subscriptionManager.selectSubscription();
        }

        currentUrl.searchParams.delete('purchase_type');
        history.pushState({}, '', currentUrl.toString());
      }

      onVariantChange(event) {
        event.preventDefault();

        window.isVariantSwitching = true;

        // V8 mode detection — a group-model product may render a formula row
        // with no size cards at all, so probe both card types.
        const isV8 = !!document.querySelector('.v8-size-card, .v8-formula-card');

        if (isV8) {
          // V8: toggle data-selected attribute — CSS handles visual state
          const clickedCard = event.target.closest('.v8-formula-card') || event.target.closest('.v8-size-card') || event.target;
          const isFormulaCard = !!clickedCard.classList.contains('v8-formula-card');
          const isSizeCard = !!clickedCard.classList.contains('v8-size-card');

          if (isFormulaCard) {
            document.querySelectorAll('.v8-formula-card').forEach((c) => {
              c.dataset.selected = 'false';
            });
            clickedCard.dataset.selected = 'true';
          } else if (isSizeCard) {
            document.querySelectorAll('.v8-size-card').forEach((c) => {
              c.dataset.selected = 'false';
            });
            clickedCard.dataset.selected = 'true';
          }
        } else {
          // Legacy: use background-color fill classes
          let isVariantType = false;
          [...this.productVariants].forEach((variant) => {
            if (variant.classList.contains('variant-selected-type')) {
              isVariantType = true;
            }
          });

          if (isVariantType) {
            [...this.productVariants].forEach((variant) => variant.classList.remove('variant-selected-type'));
            event.target.classList.add('variant-selected-type');
          } else {
            [...this.productVariants].forEach((variant) => variant.classList.remove('variant-selected-size'));
            event.target.classList.add('variant-selected-size');
          }

          const packagingType = event.target.getAttribute('data-packaging-type');

          if (packagingType) {
            [...this.productVariants].forEach((variant) =>
              variant.classList.remove('variant-selected-packaging')
            );
            event.target.classList.add('variant-selected-packaging');
          }
        }

        const isBottleVariant = event.target.getAttribute('data-has-subscription') === 'false';

        let targetValue = event.target.getAttribute('variant-value');
        let targetId = event.target.getAttribute('variant-id');

        this.selectedVariantOption.forEach((option) => {
          option.value = targetValue;
        });
        this.selectedVariantId.forEach((option) => {
          option.value = targetId;
        });

        // When switching formulas, use the formula card URL as-is — it already
        // points to the correct product. Size replacement only applies when
        // switching sizes within the same formula, otherwise it can corrupt
        // handles whose size formatting differs (e.g. "20-oz" vs "20oz").
        // After the formula swap, we'll re-select the matching size card.
        const isFormulaSwitch = event.target.closest('.v8-formula-card');
        if (isFormulaSwitch) {
          // Capture current size selection so we can re-apply it after the swap
          const currentSizeCard = document.querySelector('.v8-size-card[data-selected="true"]');
          window._v8PendingSizeName = currentSizeCard
            ? currentSizeCard.querySelector('.v8-size-name')?.textContent?.trim()
            : null;
        } else if (!isFormulaSwitch) {
          let selectedSizeElement = this.productForm.querySelector('#selected-size');
          let targetSize = selectedSizeElement ? selectedSizeElement.value : null;
          let match = targetSize ? targetSize.match(/(\d+-oz|\d+oz|\d+ml|d+-ml|\d+pack|d+-pack)/) : null;
          let sizeToReplace = match ? match[1] : null;

          if (sizeToReplace) {
            targetValue = targetValue.replace(/(\d+-oz|\d+oz|\d+ml|d+-ml|\d+pack|d+-pack)/, sizeToReplace);
          } else {
            console.log('No size found in the first URL to replace.');
          }
        }

        if (isBottleVariant) {
          window.switchingToBottle = true;
        }

        if (targetValue.includes('purchase_type=')) {
          window.pendingSubscriptionSelection = true;
        }

        this.renderProductInformation(targetValue);
      }
    }
  );

  // VariantSelectCard - Card-specific variant selection
  if (!customElements.get('variant-select-card')) {
    customElements.define(
      'variant-select-card',
      class VariantSelectCard extends HTMLElement {
        constructor() {
          super();

          this.productForm = this.closest('product-form');
          this.productVariants = this.querySelectorAll('product-variant');
          this.selectedVariantOption = this.querySelector('input[name="selected-variant-option"]');
          this.selectedVariantId = this.querySelector('input[name="selected-variant-id"]');
          this.variantIdInput = this.productForm?.querySelector('input[name="id"]');

          const productJsonScript = this.querySelector('script[data-product-json]');
          this.productJson = productJsonScript ? JSON.parse(productJsonScript.textContent) : null;
          this.selectedVariant = this.productJson?.variants?.find(
            (v) => v.id.toString() === this.variantIdInput?.value
          );
          this.productId = this.productJson?.id;
        }

        connectedCallback() {
          this.applyEventListeners();
        }

        applyEventListeners() {
          this.productVariants.forEach((variant) => {
            variant.addEventListener('click', this.onVariantChange.bind(this));
          });

          productPubSub.subscribe(PUB_SUB_EVENTS.purchaseTypeChange, (event) => {
            if (event.productId.toString() !== this.productId?.toString()) {
              return;
            }

            // Linked-size cards can hold the SAME product id at once (clicking a
            // size morphs one card into its sibling's product), so a product-id
            // match alone can't tell the cards apart. Only react to events that
            // originated inside this element's own card; events without a card
            // ancestor (PDP publishers) keep the product-id-only matching.
            if (event.sourceElement) {
              const eventCard = event.sourceElement.closest('card-product');
              const ownCard = this.closest('card-product');
              if (eventCard && ownCard && eventCard !== ownCard) {
                return;
              }
            }

            this.updatePrices({
              oldProductId: event.productId,
              newProductId: event.productId,
              selectedVariant: this.selectedVariant,
              isSubscriptionSelected: event.purchaseType === 'subscription'
            });
          });
        }

        async renderProductInformation() {
          if (this.variantIdInput && this.selectedVariantId) {
            this.variantIdInput.value = this.selectedVariantId.value;
          }
        }

        onVariantChange(event) {
          event.preventDefault();

          this.productVariants.forEach((variant) => variant.classList.remove('variant-selected-size'));
          event.target.classList.add('variant-selected-size');

          let targetProductHandle = event.target.getAttribute('variant-value');
          let targetVariantId = event.target.getAttribute('variant-id');

          if (this.selectedVariantOption) {
            this.selectedVariantOption.value = targetProductHandle;
          }
          if (this.selectedVariantId) {
            this.selectedVariantId.value = targetVariantId;
          }
          if (this.variantIdInput) {
            this.variantIdInput.value = targetVariantId;
          }

          this.fetchVariantData(targetProductHandle, targetVariantId);
        }

        updatePrices({ oldProductId, newProductId, selectedVariant, isSubscriptionSelected = false }) {
          console.log('updatePrices', {
            oldProductId,
            newProductId,
            selectedVariant,
            isSubscriptionSelected
          });

          let cardProduct = this.closest('card-product');
          if (!cardProduct && this.productForm) {
            cardProduct = this.productForm.closest('card-product');
          }

          if (cardProduct && selectedVariant) {
            let trynowPriceContainer = cardProduct.querySelector(`#card-trynow-price-${oldProductId}`);
            if (!trynowPriceContainer && oldProductId !== newProductId) {
              trynowPriceContainer = cardProduct.querySelector(`#card-trynow-price-${newProductId}`);
            }

            if (trynowPriceContainer) {
              if (oldProductId !== newProductId) {
                trynowPriceContainer.id = `card-trynow-price-${newProductId}`;
              }

              const trynowPriceSpan = trynowPriceContainer.querySelector('[data-trynow-price]');

              if (trynowPriceSpan) {
                trynowPriceSpan.setAttribute('data-trynow-price', selectedVariant.price);
                trynowPriceSpan.textContent = CurrencyUtils.formatMoney(selectedVariant.price);
              }

              const trynowComparePriceSpan = trynowPriceContainer.querySelector('[data-trynow-compare-price]');
              if (selectedVariant.compare_at_price && selectedVariant.compare_at_price > selectedVariant.price) {
                if (!trynowComparePriceSpan) {
                  const newCompareSpan = document.createElement('span');
                  newCompareSpan.className = 'trynow-original-price';
                  newCompareSpan.setAttribute('data-trynow-compare-price', selectedVariant.compare_at_price);
                  newCompareSpan.textContent = CurrencyUtils.formatMoney(selectedVariant.compare_at_price);
                  const flexContainer = trynowPriceContainer.querySelector('.flex');
                  if (flexContainer && trynowPriceSpan) {
                    flexContainer.insertBefore(newCompareSpan, trynowPriceSpan);
                  }
                } else {
                  trynowComparePriceSpan.setAttribute('data-trynow-compare-price', selectedVariant.compare_at_price);
                  trynowComparePriceSpan.textContent = CurrencyUtils.formatMoney(selectedVariant.compare_at_price);
                  trynowComparePriceSpan.style.display = '';
                }
              } else if (trynowComparePriceSpan) {
                trynowComparePriceSpan.style.display = 'none';
              }
            } else {
              console.warn('TryNow price container not found for product:', oldProductId);
            }
          } else {
            console.warn('TryNow update - cardProduct or selectedVariant missing:', {
              cardProduct: !!cardProduct,
              selectedVariant: !!selectedVariant
            });
          }

          // Scope to this card: sibling cards linked as size options can carry
          // the same product id (and thus the same data-price-id span) after a
          // size swap, so a document-wide query would repaint both cards.
          const priceScope = cardProduct || this.productForm || document;
          this.priceElements = Array.from(priceScope.querySelectorAll(`[data-price-id="price-${oldProductId}"]`));

          if (!this.priceElements.length) {
            return;
          }

          this.priceElements = this.priceElements.map((priceElement) => {
            priceElement.setAttribute('data-price-id', `price-${newProductId}`);

            let priceText = document.createElement('span');
            let compareText = document.createElement('span');
            compareText.classList.add('line-through', 'text-brand-secondary-350');

            // Prefer the server-computed % from the new product's <product-purchase-options>
            // data attr; fall back to legacy dict; if neither, do not apply a discount.
            // Same card scoping as above — two purchase-options elements can share
            // a data-product-id once sibling size cards hold the same product.
            const newProductEl = (cardProduct || document).querySelector(`product-purchase-options[data-product-id="${newProductId}"]`);
            const newProductAttr = newProductEl?.getAttribute('data-discount-percent');
            const newProductElDiscount = newProductAttr ? Number(newProductAttr) : 0;
            const discountPercent = isSubscriptionSelected
              ? (newProductElDiscount > 0 ? newProductElDiscount : 0) || window.subscriptionDiscounts?.[newProductId] || 0
              : 0;

            priceText.textContent = CurrencyUtils.formatMoney(
              selectedVariant.price * ((100 - discountPercent) / 100)
            );

            if (selectedVariant.compare_at_price) {
              const compareAtPrice = CurrencyUtils.formatMoney(selectedVariant.compare_at_price);
              compareText.textContent = `${compareAtPrice}`;
              priceText.prepend('\u00A0');
              priceText.prepend(compareText);
            }

            priceElement.textContent = '';
            priceElement.append(priceText);
            return priceElement;
          });
        }

        async fetchVariantData(productHandle, variantId) {
          console.log('fetchVariantData', {
            productHandle,
            variantId
          });

          // Last-click-wins guard. Rapid size clicks fire overlapping fetches
          // that resolve in network order, not click order: a slow response
          // for an earlier click can land after a fast one for the latest
          // click and overwrite the ATC price, hidden variant id, image and
          // purchase-options data — leaving the highlighted size, the price
          // and the variant that would actually be added to cart all out of
          // sync (e.g. 8oz highlighted, 32oz price, 32oz added). Stamp each
          // request and discard any response that isn't the newest.
          const requestId = (this._variantFetchSeq = (this._variantFetchSeq || 0) + 1);

          try {
            const previousProductId = this.productForm?.querySelector('input[name="product-id"]')?.value;
            const response = await fetch(appendCountryParam(`${productHandle.split('?')[0]}.js`));

            if (!response.ok) throw new Error('Network response was not ok');

            const productData = await response.json();

            if (requestId !== this._variantFetchSeq) {
              // A newer size click superseded this request — drop it.
              return;
            }

            this.selectedVariant = productData.variants.find(
              (variant) => variant.id.toString() === variantId
            );

            const cardProduct = this.closest('card-product');
            const newProductId = String(productData.id);
            this.productId = newProductId;

            if (this.selectedVariant) {
              this.updatePrices({
                oldProductId: previousProductId,
                newProductId: newProductId,
                selectedVariant: this.selectedVariant
              });

              if (cardProduct && productData.featured_image) {
                const productImages = cardProduct.querySelectorAll('img');
                productImages.forEach((img) => {
                  if (img.closest('.badge-image')) {
                    return;
                  }

                  const currentSrc = img.src;
                  const currentSrcset = img.srcset;

                  let sizeMatch = currentSrc.match(/width=(\d+)/);
                  let width = sizeMatch
                    ? sizeMatch[1]
                    : img.width || img.getAttribute('width') || '400';

                  const newImageUrl = productData.featured_image + '&width=' + width;

                  img.src = newImageUrl;

                  if (currentSrcset) {
                    const srcsetParts = currentSrcset.split(',').map((part) => {
                      const [url, descriptor] = part.trim().split(' ');
                      const widthMatch = url.match(/width=(\d+)/);
                      if (widthMatch) {
                        const srcsetWidth = widthMatch[1];
                        return `${productData.featured_image}&width=${srcsetWidth} ${descriptor}`;
                      }
                      return part;
                    });
                    img.srcset = srcsetParts.join(', ');
                  }

                  if (productData.title) {
                    img.alt = productData.title;
                  }
                });
              }

              if (newProductId !== previousProductId) {
                let cardAtcContainer = document.querySelector(`#card-atc-${previousProductId}`);

                if (cardAtcContainer) {
                  cardAtcContainer.id = `card-atc-${newProductId}`;
                }
              }

              if (this.variantIdInput) {
                this.variantIdInput.value = variantId;
              }
              if (this.selectedVariantId) {
                this.selectedVariantId.value = variantId;
              }

              const productFormIdInput = this.productForm?.querySelector('input[name="product-id"]');

              if (productFormIdInput) {
                productFormIdInput.value = newProductId;
              }
            }

            if (cardProduct) {
              const productPurchaseOptions = cardProduct.querySelector('product-purchase-options');

              if (productPurchaseOptions) {
                // V8-pattern: keep the element's data attributes in sync with the
                // displayed variant. Mirrors V8's "copy data-* attrs onto the live
                // element after swap" (see updatedV8PurchaseOptions.attributes loop
                // in the product-swap handler above). Without this, init() and
                // toggleSubscriptionOptions read stale prices/% from the original
                // variant render and clobber the correct values that updatePrices
                // wrote a few lines above.
                const newVariant = this.selectedVariant;
                if (newVariant) {
                  const skioGroup = (productData.selling_plan_groups || [])
                    .find((g) => g.app_id === 'SKIO');
                  let newSubPrice = newVariant.price;
                  if (skioGroup) {
                    const allocation = (newVariant.selling_plan_allocations || [])
                      .find((a) => a.selling_plan_group_id === skioGroup.id);
                    if (allocation && allocation.price < newVariant.price) {
                      newSubPrice = allocation.price;
                    }
                  }
                  const newDiscount = newSubPrice < newVariant.price
                    ? Math.round(((newVariant.price - newSubPrice) * 100) / newVariant.price)
                    : 0;
                  productPurchaseOptions.setAttribute('data-product-id', newProductId);
                  productPurchaseOptions.setAttribute('data-base-price', String(newVariant.price));
                  productPurchaseOptions.setAttribute('data-sub-price', String(newSubPrice));
                  productPurchaseOptions.setAttribute('data-discount-percent', String(newDiscount));
                  productPurchaseOptions.setAttribute('data-compare-price', String(newVariant.compare_at_price || 0));
                }
                productPurchaseOptions.init(newProductId, productData.selling_plan_groups);
              }
            }
          } catch (error) {
            console.error('Error fetching variant data:', error);
          }
        }
      }
    );
  }

  // VariantSelectSection - Section-specific variant selection
  if (!customElements.get('variant-select-section')) {
    customElements.define(
      'variant-select-section',
      class VariantSelectSection extends HTMLElement {
        constructor() {
          super();
        }
      }
    );
  }

  // ProductForm — custom element wrapper for the ATC form.
  // Submission is handled by v2-cart-drawer's capture-phase document submit
  // listener (cart-drawer.js). This component only needs to exist so that
  // other code can reference the <product-form> element for DOM traversal.
  if (!customElements.get('product-form')) {
    customElements.define(
      'product-form',
      class ProductForm extends HTMLElement {
        constructor() {
          super();
          this.form = this.querySelector('form');
        }
      }
    );
  }

  // StickyATC - Sticky add to cart button
  if (!customElements.get('sticky-atc')) {
    customElements.define(
      'sticky-atc',
      class StickyATC extends HTMLElement {
        constructor() {
          super();
          this.addToBagButton = this.closest('#product-atc')?.querySelector('button[name="add"]');
          this.headerHeight = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
          this.hasUserScrolled = false;
        }

        connectedCallback() {
          this.style.display = 'none';

          const scrollHandler = () => {
            this.hasUserScrolled = true;
            window.removeEventListener('scroll', scrollHandler);
          };
          window.addEventListener('scroll', scrollHandler);

          setTimeout(() => {
            const observeTarget =
              document.querySelector('#product-variant-selectors-new-pdp') ||
              document.querySelector('#type-selector-pdp-new') ||
              document.querySelector('#productTitle') ||
              this.addToBagButton;

            if (!observeTarget) return;

            const observer = new IntersectionObserver(
              (entries) => {
                entries.forEach((entry) => {
                  if (!entry.isIntersecting && this.hasUserScrolled) {
                    this.style.display = 'flex';
                    this.classList.add('lg:top-[var(--header-height)]');
                    this.classList.add('lg:bottom-auto');
                    this.classList.remove('lg:bottom-0');
                  } else if (entry.isIntersecting && this.hasUserScrolled) {
                    this.style.display = 'none';
                    this.classList.remove('lg:top-[var(--header-height)]');
                    this.classList.add('lg:bottom-0');
                    this.classList.remove('lg:bottom-auto');
                  }
                });
              },
              {
                root: null,
                rootMargin: `-${this.headerHeight} 0px 0px 0px`,
                threshold: 0
              }
            );

            observer.observe(observeTarget);
          }, 100);
        }
      }
    );
  }

  // ProductPurchaseOptions - Subscription/one-time toggle
  if (!customElements.get('product-purchase-options')) {
    customElements.define(
      'product-purchase-options',
      class ProductPurchaseOptions extends HTMLElement {
        constructor() {
          super();
        }

        connectedCallback() {
          this.productId = this.getAttribute('data-product-id');
          this.init(this.productId);
        }

        init(productId = this.productId, sellingPlanGroups = []) {
          const defaultSubscriptionBullets = [
            'Free Shipping on every subscription order',
            'Early Access to new product launches',
            'Bonus offers throughout the year'
          ];

          const oneTimeRadio = this.querySelector('[name="purchase_type"][value="one-time"]');
          const subscriptionRadio = this.querySelector('[name="purchase_type"][value="subscription"]');
          const benefitsContainer = this.querySelector('.subscription-benefits');
          const bulletsContainer = benefitsContainer?.querySelector('.subscription-bullets-container');

          // V8-pattern: restore the user's persisted subscribe/one-time choice so it
          // survives variant swaps. fetchVariantData re-calls init(newProductId, ...)
          // on the SAME element after a size click, and (post Part 1) updates the
          // element's data attrs. Without restoring `checked` here from the persisted
          // map, the radio state would still be in sync today only by accident
          // (radios aren't replaced) — and would break the moment anything ever
          // re-renders them. Persisting + restoring makes the behavior intentional.
          const persistedPurchaseType = window._cardPurchaseType[productId];
          if (persistedPurchaseType === 'subscription' && subscriptionRadio) {
            subscriptionRadio.checked = true;
            if (oneTimeRadio) oneTimeRadio.checked = false;
          } else if (persistedPurchaseType === 'one-time' && oneTimeRadio) {
            oneTimeRadio.checked = true;
            if (subscriptionRadio) subscriptionRadio.checked = false;
          }

          // Read the server-computed % from this element's own data-discount-percent attr
          // (set by product-purchase-options.liquid from variant.selling_plan_allocations).
          // The Liquid already rendered the correct label, so this only runs as a no-op
          // when the attr is present — preserving the legacy dict path for any old caller.
          const attrDiscount = Number(this.getAttribute('data-discount-percent'));
          const discountPercent = (!isNaN(attrDiscount) && attrDiscount > 0)
            ? attrDiscount
            : (window.subscriptionDiscounts?.[productId] || 0);
          const subscriptionLabel = this.querySelector(`label[data-subscription-label]`);
          if (subscriptionLabel) {
            subscriptionLabel.innerHTML = discountPercent > 0
              ? `Subscribe & Save ${discountPercent}%`
              : 'Subscribe & Save';
          }

          let subscriptionBullets = [...defaultSubscriptionBullets];
          if (window.subscriptionBulletsDict && window.subscriptionBulletsDict[productId]) {
            subscriptionBullets = window.subscriptionBulletsDict[productId];
          }

          if (bulletsContainer) {
            let allBullets = subscriptionBullets.map(
              (bullet) => `
              <p class="flex items-center w-full mt-2 text-brand-secondary-200">
                  <span class="mr-2">
                  <svg class="w-4 h-4 p-[1px] border rounded-full border-brand-secondary-200" viewBox="0 0 24 24" fill="none">
                      <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  </span>
                  <span class="text-sm font-base leading-[14px]">${bullet}</span>
              </p>
          `
            );

            if (window.subscriptionExceptionDict?.[productId]) {
              window.subscriptionExceptionDict?.[productId].forEach((bullet) => {
                allBullets.push(`
                  <p class="flex items-center w-full mt-2 text-brand-secondary-200">
                      <span class="text-xs font-base leading-[14px] italic">${bullet}</span>
                  </p>
              `);
              });
            }

            bulletsContainer.innerHTML = allBullets.join('');
          }

          const toggleSubscriptionOptions = (showSubscription) => {
            // Read productId dynamically from the live attribute, not from the
            // closure-captured init param. fetchVariantData updates this element's
            // data-product-id on every size swap, but the radio listeners — bound
            // ONCE due to our listener guard — would otherwise hold the FIRST init's
            // productId, causing pubsub events, persistence keys, and price-span
            // lookups to target the old product after a swap.
            const currentProductId = this.getAttribute('data-product-id') || productId;

            // V8-pattern: record the choice so it survives variant swaps (size click
            // → fetchVariantData → init re-runs and reads window._cardPurchaseType).
            window._cardPurchaseType[currentProductId] = showSubscription ? 'subscription' : 'one-time';

            productPubSub.publish(PUB_SUB_EVENTS.purchaseTypeChange, {
              source: 'product-purchase-options',
              // Lets card-scoped subscribers ignore events from sibling cards
              // that happen to hold the same product id (linked-size cards).
              sourceElement: this,
              productId: currentProductId,
              purchaseType: showSubscription ? 'subscription' : 'one-time'
            });

            if (benefitsContainer) {
              benefitsContainer.classList.toggle('hidden', !showSubscription);
            }

            const shopPayMessaging = document.getElementById('pdp-shop-pay-messaging');
            if (shopPayMessaging) {
              shopPayMessaging.style.display = showSubscription ? 'none' : 'block';
            }

            const subscriptionSelect = this.querySelector('.subscription-options');
            if (subscriptionSelect) {
              subscriptionSelect.classList.toggle('hidden', !showSubscription);
              const select = subscriptionSelect.querySelector('select');
              if (select) {
                select.name = showSubscription ? 'selling_plan' : '';
              }
            }

            // On collection cards, the page may render duplicate cards for the same
            // product (e.g. a hidden carousel + a visible grid). Both forms share the
            // same `id`, so the <select form="..."> attribute on the selling_plan
            // dropdown binds to the FIRST form in DOM — which is often the hidden
            // ghost. When the visible card's ATC button is clicked, FormData(form)
            // for the visible form has no selling_plan, so cart adds as one-time.
            // Fix: inject a hidden selling_plan input directly into the form that
            // contains THIS <product-purchase-options> element. The hidden input is
            // unambiguously inside the right form regardless of duplicate ids.
            try {
              const cardProduct = this.closest('card-product');
              const cardForm = cardProduct?.querySelector('form[data-type="add-to-cart-form"]');
              if (cardForm) {
                let hiddenSellingPlan = cardForm.querySelector('input[type="hidden"][data-injected-selling-plan]');
                if (showSubscription) {
                  const select = subscriptionSelect?.querySelector('select');
                  const planId = select?.value || '';
                  if (planId) {
                    if (!hiddenSellingPlan) {
                      hiddenSellingPlan = document.createElement('input');
                      hiddenSellingPlan.type = 'hidden';
                      hiddenSellingPlan.name = 'selling_plan';
                      hiddenSellingPlan.setAttribute('data-injected-selling-plan', '');
                      cardForm.appendChild(hiddenSellingPlan);
                    }
                    hiddenSellingPlan.value = planId;
                    // Keep the injected input in sync if the user changes the frequency
                    if (select && !select.dataset.injectListenerAttached) {
                      select.dataset.injectListenerAttached = '1';
                      select.addEventListener('change', () => {
                        if (hiddenSellingPlan && hiddenSellingPlan.isConnected) {
                          hiddenSellingPlan.value = select.value || '';
                        }
                      });
                    }
                  }
                } else if (hiddenSellingPlan) {
                  hiddenSellingPlan.remove();
                }
              }
            } catch (e) {
              /* swallow */
            }

            // Direct, fragment-local ATC price update. Reads the server-computed
            // prices (data-base-price / data-sub-price stamped on this element by
            // product-purchase-options.liquid from variant.selling_plan_allocations)
            // and writes them straight into [data-price-id="price-X"]. Runs AFTER
            // the pub/sub publish so that VariantSelectCard.updatePrices (which
            // clobbers innerHTML for compare_at_price=null variants) cannot
            // overwrite the strikethrough markup. Bypasses the fragile
            // VariantSelectCard subscriber path, which depends on productJson +
            // variantIdInput resolution that can fail on duplicate cards.
            try {
              const basePrice = Number(this.getAttribute('data-base-price')) || 0;
              const subPrice = Number(this.getAttribute('data-sub-price')) || 0;
              // Variant compare_at_price (data-compare-price, stamped by the card
              // purchase-options Liquid and refreshed on size swaps). HD/pro
              // wholesale products carry the retail value here, so the one-time
              // price must keep its strikethrough — this write used to drop it.
              const comparePrice = Number(this.getAttribute('data-compare-price')) || 0;
              if (basePrice > 0) {
                const fmt = (cents) => CurrencyUtils.formatMoney(cents);
                // Scope to the card that contains THIS <product-purchase-options>.
                // The page may have a hidden duplicate card with the same data-price-id;
                // a global document.querySelector would write to the hidden ghost instead
                // of the visible card. Fall back to document scope if (somehow) the
                // closest card isn't found, and as a safety net write to all matching
                // spans (including the ghost) so both stay in sync.
                const cardProduct = this.closest('card-product');
                const scope = cardProduct || document;
                const priceSpans = scope.querySelectorAll(`[data-price-id="price-${currentProductId}"]`);
                priceSpans.forEach((priceSpan) => {
                  if (showSubscription && subPrice > 0 && subPrice < basePrice) {
                    priceSpan.innerHTML =
                      `<span class="line-through text-brand-secondary-350">${fmt(basePrice)}</span>&nbsp;${fmt(subPrice)}`;
                  } else if (comparePrice > basePrice) {
                    priceSpan.innerHTML =
                      `<span class="line-through text-brand-secondary-350">${fmt(comparePrice)}</span>&nbsp;${fmt(basePrice)}`;
                  } else {
                    priceSpan.textContent = fmt(basePrice);
                  }
                });
              }
            } catch (e) {
              /* swallow — fragment may not be in DOM yet */
            }
          };

          const availableSellingPlanGroups = sellingPlanGroups?.filter((group) => group.app_id === 'SKIO');
          if (availableSellingPlanGroups?.length) {
            const subscriptionSelect = this.querySelector('.subscription-options select');
            if (subscriptionSelect) {
              subscriptionSelect.innerHTML = '';
              availableSellingPlanGroups
                .flatMap((group) => group.selling_plans)
                .forEach((plan, index) => {
                  const option = document.createElement('option');
                  option.value = plan.id;
                  option.textContent = plan.name;
                  if (index === 0) {
                    option.selected = true;
                  }
                  subscriptionSelect.appendChild(option);
                });
            }
          }

          // Always sync the UI to the (potentially refreshed) data attrs and
          // (potentially restored) radio state. This runs every init, including
          // re-inits after fetchVariantData swaps the variant.
          if (subscriptionRadio) {
            toggleSubscriptionOptions(subscriptionRadio.checked);
          }

          // V8-pattern: guard against duplicate listener registration.
          // fetchVariantData re-calls init(...) on the SAME element each size
          // switch, and the radios aren't replaced (no innerHTML swap here, unlike
          // V8 which replaces innerHTML and gets fresh elements with no listeners).
          // Without this guard, each size switch piles another listener on the same
          // radio → one user click would fire toggleSubscriptionOptions N times.
          if (subscriptionRadio && !subscriptionRadio.dataset.listenerBound) {
            subscriptionRadio.dataset.listenerBound = '1';
            subscriptionRadio.addEventListener('change', (event) => {
              toggleSubscriptionOptions(event.target.checked);
            });
          }

          if (oneTimeRadio && !oneTimeRadio.dataset.listenerBound) {
            oneTimeRadio.dataset.listenerBound = '1';
            oneTimeRadio.addEventListener('change', (event) => {
              toggleSubscriptionOptions(!event.target.checked);
            });
          }
        }
      }
    );
  }

  // ============================================================================
  // V8 Purchase Options — Subscribe & Save / One-time Card
  // ============================================================================

  if (!customElements.get('product-purchase-options-v8')) {
    customElements.define(
      'product-purchase-options-v8',
      class ProductPurchaseOptionsV8 extends HTMLElement {
        constructor() {
          super();
        }

        connectedCallback() {
          this.productId = this.getAttribute('data-product-id');
          this.init(this.productId);
          this.applyTryLinkDefault();
        }

        // Default to One-Time on first load if the user crossed a TryLink.
        async applyTryLinkDefault(retryCount = 0) {
          if (window._v8TryLinkDefaultApplied) return;
          if (window._v8UserSelectedPurchase) return;

          // TryNow script may not be ready on a cold load — retry.
          if (!window.trynow || typeof window.trynow.hasPassedTryLink !== 'function') {
            if (retryCount < 20) {
              setTimeout(() => this.applyTryLinkDefault(retryCount + 1), 250);
            }
            return;
          }

          try {
            const hasPassedGate = await window.trynow.hasPassedTryLink();
            if (!hasPassedGate) return;
            window._v8TryLinkDefaultApplied = true;
            if (window._v8UserSelectedPurchase || this.isBottleSelected()) return;
            this.setActiveCard('one-time');
          } catch (e) {
            console.warn('TryLink default check failed:', e);
          }
        }

        init(productId) {
          this.productId = productId;
          this.setAttribute('data-product-id', productId);

          // Re-query elements (DOM may have been replaced by product swap)
          this.subscribeCard = this.querySelector('[data-purchase-card="subscription"]');
          this.onetimeCard = this.querySelector('[data-purchase-card="one-time"]');
          this.subscriptionRadio = this.querySelector('input[value="subscription"]');
          this.onetimeRadio = this.querySelector('input[value="one-time"]');
          this.benefitsList = this.querySelector('.v8-benefits-list');
          this.frequencyLabel = this.querySelector('.v8-frequency-label');
          this.bottleExplanation = this.querySelector('.v8-bottle-explanation');

          // Re-bind events on the fresh DOM elements
          this.bindEvents();

          // Check if selected size is a bottle (bottles don't support subscription)
          const isBottle = this.isBottleSelected();

          this.updateSubscriptionVisibility(isBottle);
          this.updateBottleExplanation();

          // setActiveCard calls updateAllPriceDisplays, updateSizeGridPrices,
          // and updateATCPrice — no need to call them separately here.
          {
            // Use the persisted purchase type from window (survives DOM replacement),
            // falling back to subscription as the default on first load —
            // but only if subscription is available for the selected product.
            // No subscribe card at all (non-SKIO plans like TryNow render a
            // one-time-only element) must also force one-time: activating the
            // absent subscription card would uncheck the one-time radio and
            // leave nothing selected.
            const noSubscribeCard = !this.subscribeCard;
            const defaultType = isBottle || noSubscribeCard ? 'one-time' : 'subscription';
            const savedType = window._v8PurchaseType || defaultType;
            // If no subscription available, force one-time regardless of saved type
            const effectiveType = isBottle || noSubscribeCard ? 'one-time' : savedType;
            this.setActiveCard(effectiveType);
          }

        }

        /**
         * Check if the currently selected size card is a bottle product.
         */
        isBottleSelected() {
          const selectedCard = document.querySelector('.v8-size-card[data-selected="true"]');
          // Use data-has-subscription to determine if subscription is available,
          // rather than assuming all bottles lack subscription
          return selectedCard?.getAttribute('data-has-subscription') === 'false';
        }

        /**
         * Show or hide the subscription card based on whether the product supports it.
         * Bottles don't support subscription — only refill pouches do.
         */
        updateSubscriptionVisibility(isBottle) {
          if (this.subscribeCard) {
            this.subscribeCard.style.display = isBottle ? 'none' : '';
          }
        }

        bindEvents() {
          // Click subscribe card
          this.subscribeCard?.addEventListener('click', (e) => {
            if (e.target.tagName === 'SELECT' || e.target.tagName === 'OPTION') return;
            window._v8UserSelectedPurchase = true;
            this.setActiveCard('subscription');
          });

          // Click one-time card
          this.onetimeCard?.addEventListener('click', () => {
            window._v8UserSelectedPurchase = true;
            this.setActiveCard('one-time');
          });

          // Prevent card toggle when interacting with frequency dropdown
          const freqSelect = this.querySelector('select[data-subscription-select]');
          freqSelect?.addEventListener('click', (e) => e.stopPropagation());
        }

        isSubscription() {
          return this.subscriptionRadio?.checked || false;
        }

        setActiveCard(type) {
          const isSub = type === 'subscription';

          // Persist choice so it survives DOM replacement during product swaps
          window._v8PurchaseType = type;

          // Update hidden radios
          if (this.subscriptionRadio) this.subscriptionRadio.checked = isSub;
          if (this.onetimeRadio) this.onetimeRadio.checked = !isSub;

          // Toggle data-active on cards — CSS handles all visual state (border, etc.)
          if (this.subscribeCard) this.subscribeCard.dataset.active = String(isSub);
          if (this.onetimeCard) this.onetimeCard.dataset.active = String(!isSub);

          // Toggle data-active on radio visuals — CSS handles border/background
          const subRadioVisual = this.querySelector('[data-radio-visual="subscription"]');
          const otRadioVisual = this.querySelector('[data-radio-visual="one-time"]');
          if (subRadioVisual) subRadioVisual.dataset.active = String(isSub);
          if (otRadioVisual) otRadioVisual.dataset.active = String(!isSub);

          // Show/hide subscription details via data-visible
          const subDetails = this.querySelector('.v8-sub-details');
          if (subDetails) subDetails.dataset.visible = String(isSub);

          // Update selling_plan name attribute
          const sellingPlanSelect = this.querySelector('select[data-subscription-select]');
          if (sellingPlanSelect) {
            sellingPlanSelect.name = isSub ? 'selling_plan' : '';
          }

          // Hide Shop Pay messaging when subscription selected
          const shopPayMessaging = document.getElementById('pdp-shop-pay-messaging');
          if (shopPayMessaging) {
            shopPayMessaging.style.display = isSub ? 'none' : 'block';
          }

          // Freebie container hidden via CSS in V8 ([id^="freebie-container"] { display: none })

          // Hide TryNow when subscription is selected
          TryNowManager.hideTryNow(isSub);

          // Publish event for other components
          productPubSub.publish(PUB_SUB_EVENTS.purchaseTypeChange, {
            source: 'product-purchase-options-v8',
            productId: this.productId,
            purchaseType: type
          });

          // Update size grid prices and purchase options prices
          this.updateSizeGridPrices(isSub);
          this.updateAllPriceDisplays();
          this.updateATCPrice();
        }

        /**
         * Resolve the effective product ID for dictionary lookups.
         * The page product (e.g., bottle) may not be in the bullets/washes dicts,
         * so we fall back to the selected size card's product ID, then try any
         * pouch size card that IS in the dictionary.
         */
        getEffectiveProductId() {
          const dicts = [window.subscriptionBulletsDict, window.subscriptionWashesDict, window.subscriptionDiscounts];
          const inDict = (id) => dicts.some(d => d && d[id] !== undefined);

          // 1. Try the page product ID
          if (inDict(this.productId)) return this.productId;

          // 2. Try the selected size card's product ID
          const selectedCard = document.querySelector('.v8-size-card[data-selected="true"]');
          const selectedId = selectedCard?.getAttribute('data-product-id');
          if (selectedId && inDict(selectedId)) return selectedId;

          // 3. Fall back to any pouch card whose ID is in a dict (smallest first)
          const pouchCards = document.querySelectorAll('.v8-size-card[data-packaging-type="pouch"]');
          for (const card of pouchCards) {
            const id = card.getAttribute('data-product-id');
            if (id && inDict(id)) return id;
          }

          return this.productId;
        }

        getDiscountPercent() {
          // Prefer the V8 element's own server-computed sub/base prices (set by
          // product-purchase-options-v8.liquid from variant.selling_plan_allocations).
          // Fall back to the legacy hardcoded dict; if neither, return 0.
          const basePrice = Number(this.getAttribute('data-base-price'));
          const subPrice = Number(this.getAttribute('data-sub-price'));
          if (basePrice > 0 && subPrice > 0 && subPrice < basePrice) {
            return Math.round(((basePrice - subPrice) * 100) / basePrice);
          }
          const effectiveId = this.getEffectiveProductId();
          return window.subscriptionDiscounts?.[effectiveId] || 0;
        }

        getWashes() {
          const effectiveId = this.getEffectiveProductId();
          return window.subscriptionWashesDict?.[effectiveId] || 24;
        }

        getBasePrice() {
          // Get the one-time price from the currently selected size card
          const selectedCard = document.querySelector('.v8-size-card[data-selected="true"]');
          if (selectedCard) {
            return parseInt(selectedCard.getAttribute('data-price')) || 0;
          }
          return 0;
        }

        updateAllPriceDisplays() {
          // Use server-rendered data attributes (actual SKIO allocation prices)
          // instead of percentage-based discount math
          const el = this.closest('product-purchase-options-v8') || this;
          const subPrice = parseInt(el.getAttribute('data-sub-price')) || 0;
          const basePrice = parseInt(el.getAttribute('data-base-price')) || 0;
          const washes = parseInt(el.getAttribute('data-washes')) || 24;

          // Subscribe card prices
          const subPriceEl = this.querySelector('.v8-sub-price');
          const subComparePriceEl = this.querySelector('.v8-sub-compare-price');
          const subPerWashEl = this.querySelector('.v8-sub-per-wash');

          if (subPriceEl) subPriceEl.textContent = CurrencyUtils.formatMoney(subPrice);
          if (subComparePriceEl) {
            if (subPrice < basePrice) {
              subComparePriceEl.textContent = CurrencyUtils.formatMoney(basePrice);
              subComparePriceEl.style.display = '';
            } else {
              subComparePriceEl.style.display = 'none';
            }
          }
          if (subPerWashEl && washes) {
            subPerWashEl.textContent = `~${CurrencyUtils.formatMoney(Math.floor(subPrice / washes))}/wash`;
          }

          // One-time card prices
          const otPriceEl = this.querySelector('.v8-onetime-price');
          const otPerWashEl = this.querySelector('.v8-onetime-per-wash');

          if (otPriceEl) otPriceEl.textContent = CurrencyUtils.formatMoney(basePrice);
          if (otPerWashEl && washes) {
            otPerWashEl.textContent = `~${CurrencyUtils.formatMoney(Math.floor(basePrice / washes))}/wash`;
          }
        }

        updateSizeGridPrices(isSub) {
          const sizeCards = document.querySelectorAll('.v8-size-card');
          sizeCards.forEach((card) => {
            const cardBasePrice = parseInt(card.getAttribute('data-price')) || 0;
            const cardSubPrice = parseInt(card.getAttribute('data-sub-price')) || cardBasePrice;
            const cardWashes = parseInt(card.getAttribute('data-washes')) || 24;
            const finalPrice = isSub ? cardSubPrice : cardBasePrice;

            const priceEl = card.querySelector('.v8-size-price');
            const perWashEl = card.querySelector('.v8-size-per-wash');

            if (priceEl) priceEl.textContent = CurrencyUtils.formatMoney(finalPrice);
            if (perWashEl && cardWashes) {
              perWashEl.textContent = `~${CurrencyUtils.formatMoney(Math.floor(finalPrice / cardWashes))}/wash`;
            }
          });
        }

        renderBenefits() {
          if (!this.benefitsList) return;

          const effectiveId = this.getEffectiveProductId();
          const bullets = window.subscriptionBulletsDict?.[effectiveId] || [];
          let html = '';

          bullets.forEach((bullet) => {
            if (!window.parseBulletToV8Benefit) return;
            const parsed = window.parseBulletToV8Benefit(bullet);
            if (!parsed) return;

            if (parsed.highlight) {
              html += `
                <div class="v8-benefit-row">
                  <span class="v8-benefit-check">&#10003;</span>
                  <span class="v8-benefit-name">${parsed.name}*</span>
                  <span class="v8-benefit-original-price">${parsed.price}</span>
                  <span class="v8-benefit-free">FREE</span>
                </div>`;
            } else {
              html += `
                <div class="v8-benefit-row">
                  <span class="v8-benefit-check">&#10003;</span>
                  <span>${parsed.name}</span>
                </div>`;
            }
          });

          // Always add "Easily edit, skip, or cancel"
          html += `
            <div class="v8-benefit-row">
              <span class="v8-benefit-check">&#10003;</span>
              <span>Easily edit, skip, or cancel</span>
            </div>`;

          this.benefitsList.innerHTML = html;
        }

        updateFrequencyLabel() {
          if (!this.frequencyLabel) return;
          const selectedCard = document.querySelector('.v8-size-card[data-selected="true"]');
          if (selectedCard) {
            const sizeName = selectedCard.querySelector('.v8-size-name');
            if (sizeName) {
              const sizeText = sizeName.textContent.trim();
              const sizeMatch = sizeText.match(/(\d+oz)/);
              const size = sizeMatch ? sizeMatch[1] : sizeText;
              this.frequencyLabel.textContent = `${size} refill pouch delivers every`;
            }
          }
        }

        updateBottleExplanation() {
          if (!this.bottleExplanation) return;
          const selectedCard = document.querySelector('.v8-size-card[data-selected="true"]');
          const isBottle = selectedCard?.getAttribute('data-packaging-type') === 'bottle';
          const hasSub = selectedCard?.getAttribute('data-has-subscription') === 'true';
          const showExplanation = isBottle && hasSub;
          this.bottleExplanation.dataset.visible = String(showExplanation);

          // Update bottle explanation text dynamically based on selected size
          if (showExplanation) {
            const sizeName = selectedCard.querySelector('.v8-size-name')?.textContent?.trim() || '';
            const sizeMatch = sizeName.match(/(\d+oz)/i);
            const sizeLabel = sizeMatch ? sizeMatch[1] : '8oz';
            const firstOrderEl = this.bottleExplanation.querySelector('.v8-bottle-explanation-value');
            const thenEl = this.bottleExplanation.querySelectorAll('.v8-bottle-explanation-value')[1];
            if (firstOrderEl) firstOrderEl.textContent = sizeName;
            if (thenEl) thenEl.textContent = sizeLabel + ' refill pouch';
          }
        }

        updateATCPrice() {
          const isSub = this.isSubscription();
          const el = this.closest('product-purchase-options-v8') || this;
          const subPrice = parseInt(el.getAttribute('data-sub-price')) || 0;
          const basePrice = parseInt(el.getAttribute('data-base-price')) || 0;
          const finalPrice = isSub ? subPrice : basePrice;

          // Scope to PDP ATC + sticky ATC only (not method/bundle cards on the page)
          const productAtc = document.querySelector('#product-atc');
          const stickyAtc = document.querySelector('sticky-atc');
          const formatted = CurrencyUtils.formatMoney(finalPrice);
          const buttonLabel = isSub ? 'Subscribe & Save' : 'Add to bag';
          [productAtc, stickyAtc].forEach((container) => {
            if (!container) return;
            const btn = container.querySelector('button[type="submit"]');
            if (btn) {
              const priceSpan = btn.querySelector('[data-price-id^="price-"]');
              const priceId = priceSpan?.getAttribute('data-price-id') || '';
              btn.innerHTML = `${buttonLabel}&nbsp;\u2014&nbsp;<span data-price-id="${priceId}">${formatted}</span>`;
            }
          });

          updateRivoEarnPoints(productAtc, finalPrice, isSub);
        }
      }
    );
  }

})();
